import {
    Component,
    OnInit,
    OnDestroy,
    ElementRef,
    HostListener,
    ChangeDetectorRef,
    ViewChild,
} from '@angular/core';
import { JobService } from '@intranet/modules/jobs/job.service';
import { Job, JobStatus } from '@intranet/modules/jobs/job.types';
import { Subject, Subscription, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ContinueEntryData, TimerEntryService } from '@intranet/modules/timer/timer-entry.service';
import { TimeEventService, TimeEventDto } from '@intranet/modules/timer/time-event.service';
import { TimerSyncService } from '@intranet/core/services/timer-sync.service';
import { DesktopAppService } from '@intranet/core/services/desktop-app.service';
import { ActionService } from '@intranet/modules/actions/action.service';
import { JobAction, ActionStatus } from '@intranet/modules/actions/action.types';
import { LoginService } from '@intranet/modules/login/login.service';
import { TimerToastService } from '@intranet/modules/timer/timer-toast/timer-toast.service';
import { RecentDescriptionsService } from '@intranet/modules/timer/recent-descriptions.service';

/** Shape used for recent events in the dropdown — adapted from API response. */
export interface RecentTimerEvent {
    jobId: number;
    jobNumber: string;
    jobTitle: string;
    clientName: string;
    description: string;
    isBillable: boolean;
    /** ISO start time. Kept here so the picker's "latest entry per job"
     *  dedup can be done against an explicit timestamp rather than relying
     *  on backend ordering. */
    startTime: string;
}

/** Converts HSL (h: 0-360, s: 0-100, l: 0-100) to a hex color string.
 *  Matches the timer-tracker page so the same job always gets the same colour. */
function hslToHex(h: number, s: number, l: number): string {
    s /= 100; l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * c).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

/** Hashes an action key (job number) to a hue (0–359) via djb2 hash.
 *  Identical to timer-tracker's hueForAction. */
function hueForAction(key: string): number {
    let h = 5381;
    for (let i = 0; i < key.length; i++) {
        h = ((h << 5) + h + key.charCodeAt(i)) >>> 0;
    }
    return h % 360;
}

export interface SelectedJob {
    jobId: number;
    jobNumber: string | number;
    jobTitle: string;
    clientName: string;
    isArchived?: boolean;
}

export interface EventGroup  { clientName: string; events: RecentTimerEvent[]; }
export interface JobGroup    { clientName: string; jobs: Job[]; }
export interface UserJob     { jobId: number; jobNumber: number; jobTitle: string; clientName: string; isBillable?: boolean; }
/** A picker job plus the data shown/searched beneath it. `actions` are the
 *  job's active actions, listed under the expand chevron — clicking one starts
 *  a timer on that job with the action's title as the description.
 *  `descriptions` (recent + session entry text) are retained for search
 *  matching only; they are no longer displayed. */
export interface JobWithDescriptions { job: UserJob; actions: JobAction[]; descriptions: string[]; }

/** Normalise text for lenient search matching: lowercase and collapse any run
 *  of whitespace and dash/hyphen variants into a single space. So "SEO Contract
 *  - 2026", "seo contract 2026" and "seo contract–2026" all become
 *  "seo contract 2026" — letting a search ignore missing/extra/differing dashes. */
export function normalizeSearchText(s: string): string {
    return (s || '').toLowerCase().replace(/[\s\-‐-―]+/g, ' ').trim();
}

/** True when every token of the (already normalised) search term appears
 *  somewhere in `haystack`. Order-independent and dash/whitespace-insensitive,
 *  so "contract 2026" matches a haystack containing "seo contract 2026". An
 *  empty term matches everything. */
export function haystackMatchesTerm(haystack: string, normalizedTerm: string): boolean {
    if (!normalizedTerm) return true;
    const norm = normalizeSearchText(haystack);
    return normalizedTerm.split(' ').every(tok => norm.includes(tok));
}

@Component({
    selector: 'timer-header',
    templateUrl: './timer-header.component.html',
    styleUrls: ['./timer-header.component.scss'],
})
export class TimerHeaderComponent implements OnInit, OnDestroy {
    @ViewChild('searchField') searchFieldRef!: ElementRef<HTMLInputElement>;
    @ViewChild('dropdownSearch') dropdownSearchRef!: ElementRef<HTMLInputElement>;
    @ViewChild('pickerSearch') pickerSearchRef!: ElementRef<HTMLInputElement>;

    recentEvents: RecentTimerEvent[] = [];
    jobs: Job[] = [];

    // Stable cached arrays — only replaced when source data changes, NOT on every CD cycle
    groupedEvents: EventGroup[] = [];
    groupedJobs: JobGroup[] = [];

    description = '';
    dropdownOpen = false;
    jobPickerOpen = false;
    descDropdownOpen = false;
    searchTerm = '';
    jobPickerSearch = '';
    descSearch = '';
    selectedActionId: number | null = null;
    jobActions: JobAction[] = [];
    filteredActions: JobAction[] = [];
    myActions: JobAction[] = [];
    waitingActions: JobAction[] = [];
    otherActions: JobAction[] = [];
    isActionsLoading = false;
    /** Custom descriptions previously typed on entries for the selected job
     *  (excludes any that duplicate an action title — those already appear in
     *  the action sections). Shown as a separate "Recent descriptions" section
     *  in the description dropdown. */
    descOptions: string[] = [];
    filteredDescOptions: string[] = [];
    /** Backend recent descriptions for the current desc-dropdown job, cached
     *  alongside `jobActions` so reopening the same job doesn't refetch. */
    private _descRecentDescs: string[] = [];
    timerDisplay = '0:00:00';
    /** True from the instant the Stop button is pressed until the server
     *  confirms (or rejects) the stop. The stop itself stays pessimistic — the
     *  entry is only committed on server confirmation — but while this is true
     *  the visible counter is frozen and the button shows a busy state, so the
     *  press registers immediately instead of the timer appearing to keep
     *  running for the server roundtrip. Reset on both success and failure. */
    stopping = false;
    isLoading = false;
    isJobPickerLoading = false;
    private _selectedJob: SelectedJob | null = null;
    get selectedJob(): SelectedJob | null { return this._selectedJob; }
    set selectedJob(value: SelectedJob | null) {
        this._selectedJob = value;
        this.persistLastJob();
    }
    private _isBillable = false;
    get isBillable(): boolean { return this._isBillable; }
    set isBillable(value: boolean) {
        this._isBillable = value;
        this.persistLastJob();
    }
    jobPickerGroupedEvents: EventGroup[] = [];
    jobPickerGroupedJobs: JobGroup[] = [];
    userJobs: UserJob[] = [];
    groupedUserJobs: { clientName: string; jobs: UserJob[] }[] = [];
    userJobsWithDescs: JobWithDescriptions[] = [];
    filteredUserJobsWithDescs: JobWithDescriptions[] = [];
    expandedJobIds = new Set<number>();
    /** Active actions per job (jobId → actions), fetched on demand for jobs
     *  whose actions aren't already loaded by loadUserJobDescriptions — i.e.
     *  non-owned recently-tracked jobs and jobs found via search — so every
     *  listed job with live actions can show them. `jobActionsLoading` guards
     *  against duplicate in-flight fetches. */
    private jobActionsCache = new Map<number, JobAction[]>();
    private jobActionsLoading = new Set<number>();
    /** Descriptions used per job, most recent first. Persisted to localStorage. */
    private sessionDescs = new Map<number, string[]>();
    private static readonly SESSION_DESCS_KEY = 'thn_session_descs';
    /** Last selected job, persisted so the pill stays after page refresh. */
    private static readonly LAST_JOB_KEY = 'thn_last_job';
    private userJobsLoaded = false;
    private loadingCount = 0;

    /** Wall-clock ms of the last local timer mutation (start/stop/switch).
     *  Poll-tick reconciliations are skipped while an API request kicked off
     *  by a local mutation is still in flight, so a stale "running timer"
     *  fetched from the backend can't overwrite the just-updated UI. */
    private lastLocalMutationAt = 0;
    /** How long after a local mutation we suppress poll-tick reconciliation.
     *  Has to cover the worst-case API roundtrip on a slow connection,
     *  otherwise a poll-tick lands after the guard expires but before the
     *  PUT commits and the optimistic UI update gets reverted by a stale
     *  fetch. We also bump this flag on the API's success callback (see
     *  `saveEvent` live branch + similar paths) so the window naturally
     *  extends to the commit point. */
    private static readonly LOCAL_MUTATION_GUARD_MS = 5000;

    // ─── Start-time edit popover ─────────────────────────────────────────────────
    //
    // Click on the running timer display opens a small popover with a Start
    // time picker (editable) and an End time picker (disabled — a live entry
    // has no end yet). Save calls the same `updateRunningTimerStartTime`
    // endpoint the calendar modal uses, so existing sync paths (other windows,
    // poll-tick, BroadcastChannel) reconcile without any extra wiring.
    //
    // Picker state mirrors the calendar modal's segmented picker — same field
    // names so the existing helper methods can be ported verbatim.
    showStartEditPopover = false;
    editStartTime = '';
    editEndTime = '';
    private initialStartTimeStr = '';
    /** Currently-focused segment id (e.g. 'start-h'). Null when blurred. */
    activeTimeSeg: string | null = null;
    /** Digits the user has typed into the active segment but not yet committed. */
    timeBuf = '';
    /** Caret-aware editing flags — show the live buffer while editing, and
     *  suppress auto-advance after a Backspace/Delete so editing in place keeps
     *  the caret put. */
    timeBufDirty = false;
    timeSuppressAdvance = false;
    timeValidationError = false;
    timeValidationMessage = '';

    private searchSub!: Subscription;
    private stopSub!: Subscription;
    private descSub!: Subscription;
    private startTimeSub!: Subscription;
    private continueSub!: Subscription;
    private liveJobSub!: Subscription;
    private syncSub?: Subscription;
    private visibilityHandler?: () => void;
    private isSendingDesc = false;
    private tickInterval: any;
    private search$ = new Subject<string>();

    private timerStartTime: Date | null = null;
    /** The server's recorded start (ms) for the running timer as last seen on a
     *  poll. Used to tell a genuine start edit (the value changes) apart from
     *  constant client↔server clock skew (the value stays the same), so our own
     *  running timer keeps its correct local anchor instead of jumping. Null
     *  when no timer is running locally / between sessions. */
    private lastSeenRemoteStartMs: number | null = null;

    constructor(
        private timeEventService: TimeEventService,
        private jobService: JobService,
        private actionService: ActionService,
        public timerEntryService: TimerEntryService,
        public loginService: LoginService,
        private cdr: ChangeDetectorRef,
        private eRef: ElementRef,
        private timerSyncService: TimerSyncService,
        private desktopApp: DesktopAppService,
        private toastService: TimerToastService,
        private recentDescriptionsService: RecentDescriptionsService,
    ) {}

    ngOnInit() {
        this.loadSessionDescs();
        this.loadLastJob();

        // Check for a running timer on load
        this.timeEventService.getRunningTimer().subscribe(res => {
            if (res.success && res.runningEvent) {
                const ev = res.runningEvent;
                this.selectedJob = {
                    jobId: ev.jobId!,
                    jobNumber: ev.jobNumber || '',
                    jobTitle: ev.jobTitle || '',
                    clientName: ev.clientName || '',
                };
                this.description = this.capitalizeFirst(ev.description || '');
                if (ev.jobId && ev.description) { this.addToSessionDescs(ev.jobId, ev.description); }
                this.isBillable = ev.isBillable;
                this.timerStartTime = TimeEventService.parseDate(ev.startTime);
                // Notify the calendar/local state so it knows a timer is running
                this.timerEntryService.addEntry({
                    jobId: ev.jobId!,
                    jobNumber: ev.jobNumber || '',
                    jobTitle: ev.jobTitle || '',
                    clientName: ev.clientName || '',
                    description: ev.description || '',
                    start: this.timerStartTime,
                    isBillable: ev.isBillable,
                });
                // A fresh tab/window starts with no memory of an earlier
                // dismissal — `addEntry` always resets `nextAlertAtMs` to one
                // threshold out from the (locally unknown) start, so on a
                // timer that's already run past 4h the very first
                // `updateTimerDisplay` below would pop the alert straight
                // back up even though the user already dismissed it
                // elsewhere. Mirror `applyRemoteTimerState`'s check here so
                // a dismissal already recorded server-side is honoured
                // before that first paint.
                if (res.longTimerDismissedAt) {
                    const dismissedAtMs = new Date(res.longTimerDismissedAt).getTime();
                    if (Number.isFinite(dismissedAtMs) && dismissedAtMs > this.timerStartTime.getTime()) {
                        this.lastDismissedAtAppliedMs = dismissedAtMs;
                        this.timerEntryService.dismissLongTimerAlert();
                    }
                }
                this.updateTimerDisplay();
                this.cdr.markForCheck();
            }
        });

        this.scheduleTick();

        this.searchSub = this.search$.pipe(
            debounceTime(250),
            distinctUntilChanged(),
            switchMap(term => {
                this.isLoading = true;
                if (this.jobPickerOpen) { this.isJobPickerLoading = true; }
                this.cdr.markForCheck();
                // sortBy: 'fuzzy' asks the backend to rank by match quality before
                // paginating — without it, results are ordered by job type/ID first
                // and truncated to resultsPerPage in that (irrelevant) order, so a
                // strong match can be silently dropped in favour of a weaker one.
                const query = { page: 1, resultsPerPage: 20, searchTerm: (term || '').trim() || undefined, sortBy: 'fuzzy' };
                return this.jobService.searchJobsSilent({ ...query, status: JobStatus.Live });
            }),
        ).subscribe(live => {
            this.jobs = [...live];
            this.rebuildGroupedJobs();
            if (this.jobPickerOpen) {
                this.rebuildJobPickerLists();
                this.isJobPickerLoading = false;
            }
            this.isLoading = false;
            this.cdr.markForCheck();
        });

        this.stopSub = this.timerEntryService.stopEntry$.subscribe(() => {
            if (!this.isStopping) { this.resetToDefault(); }
        });

        this.descSub = this.timerEntryService.updateDescription$.subscribe(desc => {
            if (!this.isSendingDesc) {
                this.description = this.capitalizeFirst(desc);
                if (desc && this.selectedJob) { this.addToSessionDescs(this.selectedJob.jobId, desc); }
                this.cdr.markForCheck();
            }
        });

        this.startTimeSub = this.timerEntryService.updateStartTime$.subscribe(start => {
            this.timerStartTime = start;
            this.updateTimerDisplay();
            this.cdr.markForCheck();
        });

        this.continueSub = this.timerEntryService.continueEntry$.subscribe(data => {
            // No-op when the running timer is already this exact job + description.
            // The server's StartTimerAsync returns the existing active timer
            // unchanged in that case (it only stops+restarts for a DIFFERENT job
            // or description — see TimeEventsController.StartTimerAsync). Without
            // this guard the local stop+restart below resets the live entry's
            // start to "now": the calendar block and header counter drop to
            // 0:00:00 and tick up from zero until a poll-tick fetches the real
            // start and snaps them back (also briefly dropping the live entry's
            // stop button). The timer is already running for this job/desc, so
            // there is genuinely nothing to do — and skipping also avoids a
            // redundant /starttimer POST that the server would no-op anyway.
            if (this.timerEntryService.isLiveTimerRunning
                && this.selectedJob?.jobId === data.jobId
                && this.description === this.capitalizeFirst(data.description || '')) {
                return;
            }
            // Clear local state for the currently running timer before
            // starting the new one — matches the pattern used by
            // startFromUserJob / startFromEvent / startFromJob /
            // restartLastTimer. We deliberately do NOT call
            // `this.stopTimer()` here: that would fire a separate HTTP
            // POST /stoptimer that races against the POST /starttimer
            // below. The server's StartTimerAsync already stops the
            // previous active timer atomically inside one transaction,
            // so the extra HTTP call is redundant — and with the
            // sub-10s discard rule it actively breaks: whichever
            // request lands last can find the just-created new timer
            // and delete it as a sub-10s artefact, leaving the user
            // with nothing running.
            if (this.timerEntryService.isLiveTimerRunning) {
                this.isStopping = true;
                this.timerEntryService.stopEntry();
                this.isStopping = false;
            }
            this.selectedJob = {
                jobId: data.jobId,
                jobNumber: data.jobNumber,
                jobTitle: data.jobTitle,
                clientName: data.clientName
            };
            this.description = this.capitalizeFirst(data.description || '');
            if (data.jobId && data.description) { this.addToSessionDescs(data.jobId, data.description); }
            this.isBillable = !!data.isBillable;
            this.startLocalTimer();
            this.timerEntryService.runningTimerSaveInFlight++;
            this.timeEventService.startTimer({
                jobId: data.jobId,
                description: data.description || '',
                isBillable: !!data.isBillable,
            }).subscribe({
                next: res => {
                    if (res.success && res.runningEvent) {
                        this.timerEntryService.updateLiveEntryId(res.runningEvent.timeEventId);
                    }
                    this.loadRecentEvents();
                },
                complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
                error: () => {
                    this.timerEntryService.runningTimerSaveInFlight--;
                    // The server never created the running timer — stop the
                    // visual one so the UI matches the (real) "no timer
                    // running" server state. Without this the local timer
                    // would keep ticking against a non-existent backend
                    // entry, and any work the user did next would be lost.
                    this.revertStartedTimer();
                    this.toastService.show('Could not start the timer — please try again.');
                },
            });
            this.cdr.markForCheck();
        });

        this.liveJobSub = this.timerEntryService.updateLiveJob$.subscribe(data => {
            // Update header pill if the change came from outside (e.g. timer page modal)
            if (!this.selectedJob || this.selectedJob.jobId !== data.jobId) {
                this.selectedJob = {
                    jobId: data.jobId,
                    jobNumber: data.jobNumber,
                    jobTitle: data.jobTitle,
                    clientName: data.clientName,
                };
                // Persist the job change to the backend. The empty subscribe
                // used to swallow errors silently — when offline this left
                // the header showing the new job while the server still had
                // the old one. We now surface a toast so the user knows.
                this.timeEventService.updateRunningTimer({ jobId: data.jobId }).subscribe({
                    error: () => this.toastService.show('Could not save the timer — please try again.'),
                });
            }
            // Always sync billable state when provided (covers both job changes
            // and the deferred isBillable update from the API response)
            if (data.isBillable != null) {
                this.isBillable = data.isBillable;
            }
            this.cdr.markForCheck();
        });

        this.loadRecentEvents();
        this.loadUserJobs();

        // Cross-window sync: another window changed the timer or an entry — refresh local state and dropdown caches.
        // `poll-tick` is the cross-process bridge (browser ↔ Electron) — reconcile timer state
        // only, since dropdown caches don't change without a real user mutation (which would
        // arrive as a `timer-changed` / `entries-changed` BroadcastChannel event).
        this.syncSub = this.timerSyncService.event$.subscribe(event => {
            if (event.type === 'poll-tick') {
                this.applyRemoteTimerState();
                return;
            }
            if (event.type === 'long-timer-dismissed') {
                // A sibling window in this browser instance dismissed the
                // alert. Mirror it locally — the in-flight TimerSyncService
                // `_suppressUntil` window stops this from echoing back.
                this.timerEntryService.dismissLongTimerAlert();
                this.lastDismissedAtAppliedMs = Date.now();
                this.cdr.markForCheck();
                return;
            }
            if (event.type === 'timer-changed') {
                this.applyRemoteTimerState();
            }
            // Reload the session-desc cache from localStorage in case another part
            // of the app (e.g. the calendar modal) wrote a new description to it.
            this.loadSessionDescs();
            // Both event types can affect the dropdown's recent-events / job-descriptions caches.
            this.loadRecentEvents();
            this.loadUserJobDescriptions();
        });

        // Cross-process sync bridge: BroadcastChannel doesn't cross between the regular
        // browser and the Electron window (separate Chromium processes), so each side
        // polls to pick up timer changes made on the other. The Electron window polls
        // unconditionally — its whole purpose is to stay in sync. The browser only polls
        // when the tab is actually visible, so the user's 50 background tabs don't all
        // hammer `getRunningTimer` every 3s; switching back to a hidden tab resumes
        // polling and the UI catches up within a tick.
        const POLL_INTERVAL_MS = 3000;
        if (this.desktopApp.isTimerApp) {
            this.timerSyncService.startPolling(POLL_INTERVAL_MS);
        } else if (typeof document !== 'undefined') {
            const syncWithVisibility = () => {
                if (document.visibilityState === 'visible') {
                    this.timerSyncService.startPolling(POLL_INTERVAL_MS);
                } else {
                    this.timerSyncService.stopPolling();
                }
            };
            syncWithVisibility();
            this.visibilityHandler = syncWithVisibility;
            document.addEventListener('visibilitychange', this.visibilityHandler);
        }
    }

    ngOnDestroy() {
        this.searchSub?.unsubscribe();
        this.stopSub?.unsubscribe();
        this.descSub?.unsubscribe();
        this.startTimeSub?.unsubscribe();
        this.continueSub?.unsubscribe();
        this.liveJobSub?.unsubscribe();
        this.syncSub?.unsubscribe();
        if (this.visibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }
        this.timerSyncService.stopPolling();
        if (this.tickInterval) { clearTimeout(this.tickInterval); }
    }

    /** Re-fetches the running timer from the backend and reconciles local state.
     *  Called when another window broadcasts a timer change, and on every poll tick. */
    private applyRemoteTimerState(): void {
        // Strongest guard: any save (start time / job / desc / billable /
        // stop) currently in flight means the local optimistic state may
        // not yet be on the server, so a fetch right now would revert it.
        // This survives arbitrarily slow API roundtrips — important when
        // the long-timer modal pops on a >4h start edit and the user
        // lingers long enough for the time-based guard to expire while
        // the PUT is still flying.
        if (this.timerEntryService.runningTimerSaveInFlight > 0) {
            return;
        }
        // Secondary time-based guard: covers mutations that don't go
        // through the in-flight counter (modal dismissals, crash recovery,
        // idle resolution etc.). Take the max of the header's own flag
        // and the service-level flag so mutations initiated by other
        // components also count.
        const lastMutation = Math.max(this.lastLocalMutationAt, this.timerEntryService.lastLocalMutationAt);
        if (Date.now() - lastMutation < TimerHeaderComponent.LOCAL_MUTATION_GUARD_MS) {
            return;
        }
        this.timeEventService.getRunningTimer().subscribe(res => {
            // Re-check the guards inside the response handler. The
            // synchronous checks above ran when the request was
            // dispatched — but the response arrives asynchronously and
            // the user may have started, stopped, or switched timers
            // in the intervening window. Without this re-check a
            // server snapshot taken before the user's mutation would
            // be applied and overwrite the just-updated local state
            // (observed as: brand-new timer briefly reverting to the
            // previous job, with the elapsed counter jumping ahead by
            // the few seconds that the wrong start time was displayed).
            if (this.timerEntryService.runningTimerSaveInFlight > 0) return;
            const lastMutationNow = Math.max(this.lastLocalMutationAt, this.timerEntryService.lastLocalMutationAt);
            if (Date.now() - lastMutationNow < TimerHeaderComponent.LOCAL_MUTATION_GUARD_MS) return;

            // Cross-process long-timer-alert dismissal: if another client
            // (e.g. desktop app while we're in the browser, or vice versa)
            // pressed Keep-running or Stop on the alert, the server now
            // holds that dismissal timestamp. Mirror it here as long as
            // the dismissal applies to the currently-running timer (i.e.
            // happened after its start) and is one we haven't applied
            // yet. Idempotent across polls.
            if (res.success && res.longTimerDismissedAt) {
                const dismissedAtMs = new Date(res.longTimerDismissedAt).getTime();
                if (Number.isFinite(dismissedAtMs)
                    && dismissedAtMs > this.lastDismissedAtAppliedMs
                    && this.timerEntryService.isLiveTimerRunning
                    && this.timerEntryService.liveStartTime
                    && dismissedAtMs > this.timerEntryService.liveStartTime.getTime()) {
                    this.lastDismissedAtAppliedMs = dismissedAtMs;
                    // Use the same local-update method as the in-modal
                    // buttons. `_longTimerDismissed$` will emit and
                    // TimerSyncService will re-broadcast — same-browser
                    // siblings already saw this via their own poll-tick,
                    // and the extra broadcast is suppressed by
                    // `_suppressUntil` when applicable. Harmless either way.
                    this.timerEntryService.dismissLongTimerAlert();
                    this.cdr.markForCheck();
                }
            }
            if (res.success && res.runningEvent) {
                const ev = res.runningEvent;
                const remoteStart = TimeEventService.parseDate(ev.startTime);
                const remoteDesc = this.capitalizeFirst(ev.description || '');

                // If the user is currently typing in the description input, their
                // unsaved text will differ from `remoteDesc`. Don't treat that as
                // an out-of-sync condition (no sense forcing a refresh) and don't
                // overwrite the input below — the typed value will be saved when
                // they blur/Enter, which is when other windows learn of it.
                const userTypingDesc = typeof document !== 'undefined'
                    && document.activeElement instanceof HTMLInputElement
                    && document.activeElement.classList.contains('thn__desc-input');

                // Already in sync? Bail out early. Reassigning timerStartTime + calling
                // updateTimerDisplay() on every poll re-anchors the wall-clock-based
                // seconds at irregular phases vs the steady 1Hz interval, causing
                // visible stutter in the live timer (e.g. 1, 2-3, 4, 5-6).
                //
                // The start-time comparison uses a small tolerance window
                // because the client's `new Date()` at click time and the
                // server's `DateTimeOffset.Now` at save time essentially
                // never agree to the millisecond — they differ by typical
                // network-and-clock drift (tens to hundreds of ms). Strict
                // equality therefore failed on virtually every poll, which
                // forced reconciliation and the very stutter the bail check
                // is meant to avoid. 1500ms is wide enough to absorb drift,
                // narrow enough to catch any genuine cross-window start-time
                // edit (which always shifts by at least a second).
                const descInSync = userTypingDesc || this.description === remoteDesc;

                // Is this our own timer, already running locally for this job?
                // If so, the local start anchor was set from THIS machine's clock
                // at click and is the correct reference for the live counter. The
                // server's StartTime is on the SERVER clock, which can differ from
                // this machine's by several seconds; adopting it would make
                // `Date.now() - start` jump by that gap (the reported bug). So we
                // keep the local anchor for our own running timer and react only
                // to a *genuine* start edit (below), not to clock skew.
                const ownRunningTimer = this.timerEntryService.isLiveTimerRunning
                    && this.selectedJob?.jobId === ev.jobId;

                // Client↔server clock offset from the server's own "now" in this
                // response. Used only when ADOPTING a timer this machine didn't
                // start (e.g. started on another device) so its elapsed is
                // measured against this machine's clock rather than inheriting the
                // gap. 0 when the server didn't send a time (older backend) — a
                // safe no-op that preserves the previous behaviour.
                const serverOffsetMs = res.serverTime
                    ? TimeEventService.parseDate(res.serverTime).getTime() - Date.now()
                    : 0;

                // Tell a genuine start change (an edit from another window/device,
                // which changes the server's recorded start) apart from constant
                // clock skew (which keeps it the same across polls). The first
                // observation only sets the baseline. Updated before the bail
                // below so the baseline tracks even when we bail out.
                const prevRemoteStartMs = this.lastSeenRemoteStartMs;
                this.lastSeenRemoteStartMs = remoteStart.getTime();
                const remoteStartChanged = prevRemoteStartMs != null
                    && Math.abs(remoteStart.getTime() - prevRemoteStartMs) > 1500;

                const startInSync = ownRunningTimer
                    ? !remoteStartChanged
                    : (this.timerStartTime != null
                        && Math.abs(this.timerStartTime.getTime() - remoteStart.getTime()) < 1500);
                if (this.timerEntryService.isLiveTimerRunning
                    && this.selectedJob?.jobId === ev.jobId
                    && descInSync
                    && this.isBillable === ev.isBillable
                    && startInSync) {
                    return;
                }

                this.selectedJob = {
                    jobId: ev.jobId!,
                    jobNumber: ev.jobNumber || '',
                    jobTitle: ev.jobTitle || '',
                    clientName: ev.clientName || '',
                };
                if (!userTypingDesc) {
                    this.description = remoteDesc;
                }
                this.isBillable = ev.isBillable;
                // Keep our own running timer's local (client-clock) anchor unless
                // its start was genuinely edited elsewhere — then adopt the new
                // value. For a timer this machine didn't start locally, convert
                // the server start to this machine's clock so the counter doesn't
                // inherit the client↔server gap.
                if (ownRunningTimer && !remoteStartChanged && this.timerStartTime != null) {
                    // keep this.timerStartTime as-is (correct local anchor)
                } else if (ownRunningTimer && remoteStartChanged) {
                    this.timerStartTime = remoteStart;
                } else {
                    this.timerStartTime = new Date(remoteStart.getTime() - serverOffsetMs);
                }

                const entryEvent = {
                    jobId: ev.jobId!,
                    jobNumber: ev.jobNumber || '',
                    jobTitle: ev.jobTitle || '',
                    clientName: ev.clientName || '',
                    description: ev.description || '',
                    start: this.timerStartTime,
                    isBillable: ev.isBillable,
                };
                if (!this.timerEntryService.isLiveTimerRunning) {
                    this.timerEntryService.addEntry(entryEvent);
                } else {
                    // Already running locally — sync the live entry fields without firing a fresh start.
                    // Use the dedicated start-time setter so subscribers
                    // (including the timer-tracker's `startTimeSub`, which
                    // moves the live FC event to its new position) are
                    // notified. Direct assignment to `liveStartTime` would
                    // miss those subscribers and the calendar block would
                    // stay at the old position until the next poll-tick
                    // managed to also force a full `loadEventsFromApi`
                    // rebuild — a race that was reliably failing in the
                    // browser when an edit was saved from the desktop app.
                    const liveStartChanged = !this.timerEntryService.liveStartTime
                        || this.timerEntryService.liveStartTime.getTime() !== this.timerStartTime.getTime();
                    if (liveStartChanged) {
                        this.timerEntryService.updateStartTime(this.timerStartTime);
                    }
                    this.timerEntryService.liveJobId = ev.jobId!;
                    this.timerEntryService.liveEntry = entryEvent;
                }
                this.updateTimerDisplay();
                this.cdr.markForCheck();
            } else if (this.timerEntryService.isLiveTimerRunning) {
                // Backend says no running timer — clear local state.
                this.lastSeenRemoteStartMs = null;
                this.isStopping = true;
                this.timerEntryService.stopEntry();
                this.isStopping = false;
                this.resetToDefault();
            }
        });
    }

    private resetToDefault() {
        // Keep selectedJob and isBillable so the user can restart the same job
        // by clicking the play button. The pill stays visible after stopping.
        // Clear the stop-in-progress freeze too — whatever path stopped the
        // timer (button, cross-window, crash recovery), the counter must be
        // free to show 0:00:00 again.
        this.stopping = false;
        this.timerStartTime = null;
        this.description = '';
        this.timerDisplay = '0:00:00';
        this.jobActions = [];
        this.filteredActions = [];
        this.myActions = [];
        this.waitingActions = [];
        this.otherActions = [];
        this.descOptions = [];
        this.filteredDescOptions = [];
        this._descRecentDescs = [];
        this.selectedActionId = null;
        this._descJobId = null;
        if (this.descDropdownOpen) { this.closeDescDropdown(); }
        // The start-edit popover only makes sense while a timer is running —
        // hide it as soon as the timer stops (locally or from another window).
        if (this.showStartEditPopover) { this.closeStartEditPopover(); }
        this.cdr.markForCheck();
    }

    /** Schedule the next counter repaint aligned to just past the next whole-
     *  second boundary of the elapsed time, then reschedule from inside the
     *  callback. Unlike a fixed-cadence setInterval, this re-aligns after any
     *  main-thread stall (a poll response, the undo-toast auto-dismissing,
     *  another component's change detection) instead of letting the 1-second
     *  cadence drift — which the user perceives as the counter lingering on a
     *  value and then jumping ahead to "catch up". Kept inside the Angular zone
     *  so change-detection behaviour is identical to the previous setInterval. */
    private scheduleTick(): void {
        if (this.tickInterval) { clearTimeout(this.tickInterval); }
        const start = this.timerEntryService.liveStartTime || this.timerStartTime;
        let delay = 1000;
        if (start) {
            const elapsed = Date.now() - new Date(start as any).getTime();
            // Normalise the in-second remainder to [0,999] so a start that is a
            // hair in the future (clock edge cases) can't produce a >1s wait.
            const intoSecond = ((elapsed % 1000) + 1000) % 1000;
            // +20ms cushion so Math.floor has definitely crossed the boundary
            // by the time updateTimerDisplay reads the clock.
            delay = 1000 - intoSecond + 20;
        }
        this.tickInterval = setTimeout(() => {
            this.updateTimerDisplay();
            this.scheduleTick();
        }, delay);
    }

    private updateTimerDisplay() {
        // Stop pressed: freeze the counter on its current value until the
        // server confirms (or rejects) the stop, so the press feels immediate.
        // The pessimistic commit still happens in stopTimer()'s callbacks.
        if (this.stopping) {
            return;
        }
        const start = this.timerEntryService.liveStartTime || this.timerStartTime;
        if (!start) {
            this.timerDisplay = '0:00:00';
            return;
        }
        const ms = Date.now() - new Date(start as any).getTime();
        const totalSec = Math.max(0, Math.floor(ms / 1000));
        const h = Math.floor(totalSec / 3600);
        const m = (Math.floor(totalSec / 60) % 60).toString().padStart(2, '0');
        const s = (totalSec % 60).toString().padStart(2, '0');
        this.timerDisplay = `${h}:${m}:${s}`;

        // Long-running timer alert check (repeats every threshold interval)
        const elapsedMs = Date.now() - new Date(start as any).getTime();
        if (!this.timerEntryService.showLongTimerAlert && elapsedMs >= this.timerEntryService.nextAlertAtMs) {
            // Calculate the next threshold (skip past current elapsed to avoid stacking on page load)
            const intervals = Math.floor(elapsedMs / this.timerEntryService.LONG_TIMER_THRESHOLD_MS);
            this.timerEntryService.nextAlertAtMs = (intervals + 1) * this.timerEntryService.LONG_TIMER_THRESHOLD_MS;
            this.timerEntryService.showLongTimerAlert = true;
            this.cdr.markForCheck();
        }
    }

    // ── Data loading ─────────────────────────────────────────────────────────────

    /** Loads the recent time entries that back the dropdown's "Previously
     *  tracked" list. Pass `silent` to refresh in place without the loading
     *  spinner — used when reopening the dropdown, where the existing list is
     *  already on screen and we just want it to pick up edits (e.g. a
     *  description just typed in the timer-tracker in the same window, which
     *  BroadcastChannel doesn't echo back to us). */
    loadRecentEvents(silent = false) {
        if (!silent) { this.startLoading(); }
        // Fetch a wide window so the dropdown's recent-jobs list survives
        // sessions where the user switches frequently between a few jobs
        // (otherwise the unique-jobs count after dedup-by-jobId can fall to
        // just those few even though older jobs were tracked earlier).
        this.timeEventService.getRecentEvents(100).subscribe(res => {
            if (res.success) {
                // Sort newest-first by startTime (ISO 8601 strings sort
                // chronologically in lexicographic order). Subsequent dedup
                // by jobId in `rebuildJobPickerLists` and `rebuildGroupedEvents`
                // keeps the *first* match — i.e. the latest entry — per job.
                this.recentEvents = res.events
                    .map(ev => ({
                        jobId: ev.jobId!,
                        jobNumber: String(ev.jobNumber || ''),
                        jobTitle: ev.jobTitle || '',
                        clientName: ev.clientName || '',
                        description: ev.description || '',
                        isBillable: !!ev.isBillable,
                        startTime: ev.startTime || '',
                    }))
                    .sort((a, b) => b.startTime.localeCompare(a.startTime));
            }
            this.rebuildGroupedEvents();
            // If the user-jobs list has already been built, re-apply the
            // picker filter so its latest-tracked ordering picks up the
            // freshly-loaded recentEvents.
            if (this.userJobsWithDescs.length) {
                this.filterUserJobsWithDescs(this.jobPickerSearch || '');
            }
            if (!silent) { this.stopLoading(); }
            this.cdr.markForCheck();
        });
    }

    /** Loads the jobs where the current user owns actions (assigned to them). */
    loadUserJobs() {
        const userId = this.loginService.userId;
        if (!userId) return;
        this.actionService.fetchActionsForOwner(userId).subscribe(actions => {
            // Extract unique jobs from actions, ordered by most recent scheduleDate
            const jobMap = new Map<number, { job: UserJob; latestDate: number }>();
            for (const a of actions) {
                if (!a.jobId) continue;
                const existing = jobMap.get(a.jobId);
                const date = a.scheduleDate ? new Date(a.scheduleDate).getTime() : 0;
                if (!existing || date > existing.latestDate) {
                    jobMap.set(a.jobId, {
                        job: {
                            jobId: a.jobId,
                            jobNumber: a.jobNumber,
                            jobTitle: a.jobTitle,
                            clientName: a.clientName || '',
                            isBillable: !!a.isBillable,
                        },
                        latestDate: date
                    });
                }
            }
            // Sort by most recent first
            this.userJobs = Array.from(jobMap.values())
                .sort((a, b) => b.latestDate - a.latestDate)
                .map(entry => entry.job);
            this.rebuildGroupedUserJobs();
            this.userJobsLoaded = true;
            this.loadUserJobDescriptions();
            this.cdr.markForCheck();
        });
    }

    private rebuildGroupedUserJobs(term?: string) {
        // Normalised, token-based matching (see normalizeSearchText) so dashes,
        // extra spaces and casing don't matter. Also matches a job by its live
        // action titles / recent descriptions so this "start a timer" search
        // behaves like the job picker — actions from the shared cache,
        // descriptions from the already-loaded userJobsWithDescs entry.
        const normTerm = normalizeSearchText(term);
        const filtered = normTerm
            ? this.userJobs.filter(j => {
                const acts = this.jobActionsCache.get(j.jobId) || [];
                const jwd = this.userJobsWithDescs.find(x => x.job.jobId === j.jobId);
                return haystackMatchesTerm([
                    String(j.jobNumber),
                    j.jobTitle || '',
                    j.clientName || '',
                    ...acts.map(a => a.title || ''),
                    ...(jwd?.descriptions || []),
                ].join(' '), normTerm);
            })
            : this.userJobs;
        const map = new Map<string, UserJob[]>();
        for (const j of filtered) {
            const key = j.clientName || 'Unknown';
            if (!map.has(key)) { map.set(key, []); }
            map.get(key)!.push(j);
        }
        this.groupedUserJobs = Array.from(map.entries())
            .map(([clientName, jobs]) => ({ clientName, jobs }));
    }

    /** Loads action titles + recent time-entry descriptions for each user job (for the job picker dropdown). */
    private loadUserJobDescriptions() {
        // Pull the latest session-desc cache from localStorage in case another part
        // of the app (e.g. calendar modal) just wrote a new description.
        this.loadSessionDescs();
        if (!this.userJobs.length) {
            // No owned jobs to fetch action/desc data for, but still rebuild the
            // picker so a non-owned running job's session-cached descriptions
            // refresh immediately (e.g. user with no assigned actions tracking
            // a colleague's job).
            this.filterUserJobsWithDescs(this.jobPickerSearch || '');
            this.cdr.markForCheck();
            return;
        }
        const obs = this.userJobs.map(j =>
            forkJoin({
                actions: this.actionService.fetchAllForJob(j.jobId),
                recent: this.timeEventService.getRecentDescriptions(j.jobId, 10),
            }).pipe(
                map(({ actions, recent }) => {
                    // Active actions only (exclude Completed/Archived), most-
                    // recently-updated first. These are what the picker now
                    // lists under the job's expand chevron. Also cached by
                    // jobId so the same job shown via search reuses them.
                    const activeActions = this.activeActionsSorted(actions);
                    this.jobActionsCache.set(j.jobId, activeActions);

                    // Recent + session entry descriptions — retained for search
                    // matching only (no longer displayed). Skip dash/blank-only
                    // text and dedupe case-insensitively.
                    const maxDescs = 10;
                    const seen = new Set<string>();
                    const descriptions: string[] = [];
                    const isMeaningful = (s: string) => !!s && !/^[\s\-–—]+$/.test(s);
                    const sessionList = this.sessionDescs.get(j.jobId) || [];
                    for (const d of sessionList) {
                        if (descriptions.length >= maxDescs) break;
                        if (isMeaningful(d) && !seen.has(d.toLowerCase())) {
                            seen.add(d.toLowerCase());
                            descriptions.push(d);
                        }
                    }
                    for (const d of (recent.descriptions || [])) {
                        if (descriptions.length >= maxDescs) break;
                        const desc = this.capitalizeFirst((d || '').trim());
                        if (isMeaningful(desc) && !seen.has(desc.toLowerCase())) {
                            seen.add(desc.toLowerCase());
                            descriptions.push(desc);
                        }
                    }

                    return { job: j, actions: activeActions, descriptions } as JobWithDescriptions;
                })
            )
        );
        forkJoin(obs).subscribe(results => {
            this.userJobsWithDescs = results;
            // Initial filtered list — same merge/sort as the post-search
            // path so owned + recently-tracked non-owned jobs share one
            // ordered list.
            this.filterUserJobsWithDescs(this.jobPickerSearch || '');
            this.cdr.markForCheck();
        });
    }

    /** Active actions (excluding Completed/Archived), most-recently-updated
     *  first — the list shown under a job's expand chevron in the picker. */
    private activeActionsSorted(actions: JobAction[]): JobAction[] {
        return [...actions]
            .filter(a => a.actionStatus !== ActionStatus.Completed
                      && a.actionStatus !== ActionStatus.Archived)
            .sort((a, b) => {
                const da = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
                const db = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
                return db - da;
            });
    }

    /** Fetch active actions for the non-owned recently-tracked jobs in the
     *  picker so they list their actions too (owned jobs get theirs in
     *  loadUserJobDescriptions). A job the current user doesn't own an action
     *  on — e.g. a colleague's job they've tracked time against — still has
     *  live actions worth showing. Fetches once per job (cached + guarded) and
     *  refreshes the displayed list as each result arrives. */
    private loadNonOwnedJobActions(): void {
        for (const jobId of this.knownNonOwnedJobs.keys()) {
            this.loadJobActions(jobId);
        }
    }

    /** Fetch and cache a job's active actions on demand (guarded against
     *  duplicate fetches), for jobs whose actions aren't already loaded by
     *  loadUserJobDescriptions — non-owned recently-tracked jobs and search
     *  results. Re-runs the picker filter so a My-Jobs entry re-attaches its
     *  actions, and marks for check so the search-result rows update once the
     *  actions arrive. */
    private loadJobActions(jobId: number): void {
        if (!jobId) return;
        if (this.jobActionsCache.has(jobId) || this.jobActionsLoading.has(jobId)) return;
        this.jobActionsLoading.add(jobId);
        this.actionService.fetchAllForJob(jobId).subscribe({
            next: actions => {
                this.jobActionsCache.set(jobId, this.activeActionsSorted(actions));
                this.jobActionsLoading.delete(jobId);
                this.filterUserJobsWithDescs(this.jobPickerSearch || '');
                this.cdr.markForCheck();
            },
            error: () => {
                this.jobActionsCache.set(jobId, []);
                this.jobActionsLoading.delete(jobId);
            },
        });
    }

    /** Active actions cached for a job (used by the search-result rows to show
     *  their expand chevron + list). Empty until loaded by loadJobActions. */
    actionsForJob(jobId: number): JobAction[] {
        return this.jobActionsCache.get(jobId) || [];
    }

    private fetchJobs(term: string) {
        this.startLoading();
        if (this.jobPickerOpen) { this.isJobPickerLoading = true; }
        // sortBy: 'fuzzy' — see the comment on the equivalent search$ pipeline
        // above: ranks by match quality before the resultsPerPage truncation.
        const query = { page: 1, resultsPerPage: 20, searchTerm: term || undefined, sortBy: 'fuzzy' };
        this.jobService.searchJobsSilent({ ...query, status: JobStatus.Live }).subscribe(live => {
            this.jobs = [...live];
            this.rebuildGroupedJobs();
            if (this.jobPickerOpen) {
                this.rebuildJobPickerLists();
                this.isJobPickerLoading = false;
            }
            this.stopLoading();
            this.cdr.markForCheck();
        });
    }

    private startLoading() {
        this.loadingCount++;
        this.isLoading = true;
    }

    private stopLoading() {
        this.loadingCount = Math.max(0, this.loadingCount - 1);
        if (this.loadingCount === 0) { this.isLoading = false; }
    }

    // ── Stable cache builders — called only when data changes ─────────────────

    private rebuildGroupedEvents() {
        const term = this.searchTerm.toLowerCase();
        const filtered = term
            ? this.recentEvents.filter(e =>
                (e.jobTitle    || '').toLowerCase().includes(term) ||
                (e.clientName  || '').toLowerCase().includes(term) ||
                (e.jobNumber   || '').toLowerCase().includes(term) ||
                (e.description || '').toLowerCase().includes(term))
            : this.recentEvents;

        // Skip entries with no job, deduplicate by jobId, exclude jobs already in My Jobs
        const userJobIds = new Set(this.userJobs.map(j => j.jobId));
        const seenJobIds = new Set<number>();
        const unique: RecentTimerEvent[] = [];
        for (const evt of filtered) {
            if (!evt.jobId || !evt.jobTitle) continue;
            if (userJobIds.has(evt.jobId)) continue;
            if (seenJobIds.has(evt.jobId)) continue;
            seenJobIds.add(evt.jobId);
            unique.push(evt);
        }

        const map = new Map<string, RecentTimerEvent[]>();
        for (const evt of unique) {
            const key = evt.clientName || 'Unknown';
            if (!map.has(key)) { map.set(key, []); }
            map.get(key).push(evt);
        }
        this.groupedEvents = Array.from(map.entries())
            .map(([clientName, events]) => ({ clientName, events }));
    }

    private rebuildGroupedJobs() {
        // Exclude jobs already shown in a section above so a searched job that
        // is also in one of them isn't listed twice under "Other jobs":
        //  - the user's own jobs ("My Jobs"), and
        //  - jobs already listed under "Previously tracked time entries"
        //    (groupedEvents). Without this second exclusion a job in the recent
        //    ("top 20") list that the live job-search API also returns appears
        //    once under Previously tracked and again here — the reported dupe.
        // groupedEvents is always rebuilt (rebuildGroupedEvents) before this
        // runs on every search path, so it reflects the current filter.
        const shownJobIds = new Set<number>(this.userJobs.map(j => j.jobId));
        for (const group of this.groupedEvents) {
            for (const evt of group.events) { shownJobIds.add(evt.jobId); }
        }
        const map = new Map<string, Job[]>();
        for (const job of this.jobs) {
            if (shownJobIds.has(job.jobId)) continue;
            // Group under the client's internal/display name, falling back to
            // the legal name.
            const key = job.client?.displayName || job.client?.clientName || 'Unknown';
            if (!map.has(key)) { map.set(key, []); }
            map.get(key).push(job);
        }
        // Live jobs first, then archived within each client group
        this.groupedJobs = Array.from(map.entries())
            .map(([clientName, jobs]) => ({
                clientName,
                jobs: jobs.sort((a, b) => (a.archive ? 1 : 0) - (b.archive ? 1 : 0)),
            }));
    }

    // ── TrackBy functions ─────────────────────────────────────────────────────

    trackByClient(_: number, g: EventGroup | JobGroup): string {
        return g.clientName;
    }

    trackByEvent(_: number, e: RecentTimerEvent): string {
        return `${e.jobId}-${e.description}`;
    }

    trackByJob(_: number, j: Job): number {
        return j.jobId;
    }

    trackByUserJob(_: number, j: UserJob): number {
        return j.jobId;
    }

    trackByAction(_: number, a: JobAction): number | string {
        return a.jobActionId ?? a.title ?? _;
    }

    // ── Colours (matches timer-tracker page) ────────────────────────────────────

    jobColor(jobNumber: string | number): string {
        const h = hueForAction(String(jobNumber ?? ''));
        return hslToHex(h, 87, 33);
    }

    jobPillBg(jobNumber: string | number): string {
        const h = hueForAction(String(jobNumber ?? ''));
        return hslToHex(h, 60, 88);
    }

    // ── Dropdown ─────────────────────────────────────────────────────────────────

    focusSearch() {
        this.searchFieldRef?.nativeElement.focus();
    }

    onSearchInput() {
        // Rebuild event filter immediately (client-side); API search is debounced
        this.rebuildGroupedEvents();
        this.rebuildGroupedUserJobs(this.searchTerm);
        // Only search all jobs via API when there's a search term
        if (this.searchTerm) {
            this.search$.next(this.searchTerm);
        } else {
            this.jobs = [];
            this.groupedJobs = [];
        }
        if (!this.dropdownOpen) { this.dropdownOpen = true; }
    }

    openDropdown() {
        if (this.jobPickerOpen) { this.closeJobPicker(); }
        if (this.descDropdownOpen) { this.closeDescDropdown(); }
        if (!this.dropdownOpen) {
            this.searchTerm = '';
            this.jobs = [];
            this.groupedJobs = [];
            this.rebuildGroupedEvents();
            this.rebuildGroupedUserJobs();
            // Always refresh the recent entries on open so descriptions edited
            // elsewhere in THIS window (e.g. the timer-tracker list, whose
            // BroadcastChannel notify doesn't echo back to us) show up. Show
            // the cached list immediately; refresh silently when we already
            // have one so it doesn't flash the loading spinner.
            this.loadRecentEvents(this.recentEvents.length > 0);
            if (!this.userJobsLoaded) { this.loadUserJobs(); }
        }
        this.dropdownOpen = true;
        setTimeout(() => this.dropdownSearchRef?.nativeElement.focus());
    }

    closeDropdown() {
        this.dropdownOpen = false;
        this.searchTerm = '';
        this.jobs = [];
        this.groupedJobs = [];
        this.rebuildGroupedEvents(); // reset any search filter
    }

    onEnterKey() {
        const evtMatches = this.groupedEvents.flatMap(g => g.events);
        const jobMatches = this.groupedJobs.flatMap(g => g.jobs);
        if (evtMatches.length === 1) {
            this.startFromEvent(evtMatches[0]);
        } else if (jobMatches.length === 1) {
            this.startFromJob(jobMatches[0]);
        } else {
            this.closeDropdown();
        }
    }

    // ── Job picker (right-side pill dropdown) ───────────────────────────────────

    toggleJobPicker(event: Event) {
        event.stopPropagation();
        if (this.jobPickerOpen) {
            this.closeJobPicker();
        } else {
            this.openJobPicker();
        }
    }

    openJobPicker() {
        if (this.dropdownOpen) { this.closeDropdown(); }
        if (this.descDropdownOpen) { this.closeDescDropdown(); }
        this.jobPickerOpen = true;
        this.jobPickerSearch = '';
        this.jobs = [];
        this.groupedJobs = [];
        // Collapse any actions expanded on a previous open so the picker always
        // reopens with every job's actions hidden.
        this.expandedJobIds.clear();
        this.rebuildJobPickerLists();
        this.rebuildGroupedUserJobs();
        if (!this.userJobsLoaded) { this.loadUserJobs(); }
        // Always refresh descriptions on open so edits made elsewhere
        // (e.g. calendar modal) show up without needing a page refresh.
        if (this.userJobs.length) {
            this.loadUserJobDescriptions();
        }
        // Apply the merge+sort up front so the order is stable from the
        // moment the dropdown opens — `loadUserJobDescriptions` finishes
        // async and would otherwise re-sort the list a tick later, causing
        // a visible reorder on each open.
        this.filterUserJobsWithDescs(this.jobPickerSearch || '');
        setTimeout(() => this.pickerSearchRef?.nativeElement.focus());
    }

    closeJobPicker() {
        this.jobPickerOpen = false;
        this.jobPickerSearch = '';
        this.isJobPickerLoading = false;
    }

    onJobPickerSearch() {
        this.rebuildJobPickerLists();
        this.rebuildGroupedUserJobs(this.jobPickerSearch);
        this.filterUserJobsWithDescs(this.jobPickerSearch);
        if (this.jobPickerSearch) {
            this.isJobPickerLoading = true;
            this.search$.next(this.jobPickerSearch);
        } else {
            this.jobs = [];
            this.groupedJobs = [];
            this.jobPickerGroupedJobs = [];
        }
    }

    private filterUserJobsWithDescs(term: string) {
        // Normalised, token-based matching so "contract 2026" matches
        // "SEO Contract - 2026" (missing/differing dashes), and a stray leading
        // space or different casing still matches.
        const normTerm = normalizeSearchText(term);
        // Match across job number, job title, client name, the job's action
        // titles, and its recent descriptions — folded into one haystack so a
        // multi-word term can match across fields (e.g. "bentley seo"). Actions
        // are what's displayed; descriptions stay searchable (a job is often
        // remembered by the work tracked against it). All are already attached
        // to each `JobWithDescriptions` entry, so no new data fetch.
        const matches = (jwd: JobWithDescriptions) => haystackMatchesTerm([
            String(jwd.job.jobNumber),
            jwd.job.jobTitle || '',
            jwd.job.clientName || '',
            ...(jwd.actions || []).map(a => a.title || ''),
            ...(jwd.descriptions || []),
        ].join(' '), normTerm);

        // Merge owned jobs with non-owned recently-tracked jobs into one
        // unified list. Once entries exist for any job (owned or not), the
        // most recent ones bubble to the top.
        const merged: JobWithDescriptions[] = [
            ...this.userJobsWithDescs.filter(matches),
            ...this.getRecentNonOwnedJobsWithDescs().filter(matches),
        ];

        // When the user isn't actively searching, only show jobs they've
        // recently tracked. The picker is meant to surface ongoing work —
        // owned jobs they haven't started a timer on yet are accessible via
        // search but don't take up dropdown space by default. This also
        // makes the cross-refresh experience match the in-session one,
        // since the recent set comes straight from the API.
        let visible = merged;
        if (!normTerm) {
            const recentMap = this.getJobLatestTrackedMap();
            visible = merged.filter(jwd => recentMap.has(jwd.job.jobId));
        }

        this.filteredUserJobsWithDescs = this.sortJobsByLatestTracked(visible, jwd => jwd.job.jobId);

        // Kick off action fetches for any non-owned jobs we haven't loaded yet
        // so they list their actions too. Guarded/cached, so this is a cheap
        // no-op once each has been fetched.
        this.loadNonOwnedJobActions();
    }

    /** Component-level cache of non-owned jobs seen in recentEvents.
     *  Accumulates over the session so that a job doesn't disappear from the
     *  picker just because newer entries pushed it out of the server's
     *  last-20 recentEvents window (e.g. repeatedly switching between two
     *  jobs). Entries are pruned only if a job later becomes owned. */
    private knownNonOwnedJobs = new Map<number, JobWithDescriptions>();
    private readonly NON_OWNED_MAX_DESCS = 10;

    /** Builds JobWithDescriptions entries for jobs the user has recent time
     *  entries on but doesn't own. These are merged into the unified picker
     *  list above so non-owned jobs aren't stuck in a separate section. */
    private getRecentNonOwnedJobsWithDescs(): JobWithDescriptions[] {
        const userJobIds = new Set(this.userJobs.map(j => j.jobId));

        // Fold the current recentEvents window into the cache. New jobs get
        // added; descriptions accumulate per-job (deduped, capped).
        for (const evt of this.recentEvents) {
            if (!evt.jobId || !evt.jobTitle) continue;
            if (userJobIds.has(evt.jobId)) continue;
            let entry = this.knownNonOwnedJobs.get(evt.jobId);
            if (!entry) {
                entry = {
                    job: {
                        jobId: evt.jobId,
                        jobNumber: parseInt(evt.jobNumber, 10) || 0,
                        jobTitle: evt.jobTitle,
                        clientName: evt.clientName || '',
                        isBillable: !!evt.isBillable,
                    },
                    // Non-owned jobs don't have their actions fetched, so no
                    // action list is shown for them; they stay searchable via
                    // their recent descriptions below.
                    actions: [],
                    descriptions: [],
                };
                this.knownNonOwnedJobs.set(evt.jobId, entry);
            }
            if (entry.descriptions.length >= this.NON_OWNED_MAX_DESCS) continue;
            const desc = (evt.description || '').trim();
            if (!desc) continue;
            const key = desc.toLowerCase();
            const dup = entry.descriptions.some(d => d.toLowerCase() === key);
            if (dup) continue;
            entry.descriptions.push(desc);
        }

        // Bootstrap the running non-owned job so its session descriptions
        // can surface immediately. Without this, a brand-new timer on a
        // non-owned job has no entry in recentEvents yet and would only
        // appear in the picker after the timer stops.
        const live = this.selectedJob;
        if (live && this.timerEntryService.isLiveTimerRunning
                && !userJobIds.has(live.jobId)
                && !this.knownNonOwnedJobs.has(live.jobId)) {
            this.knownNonOwnedJobs.set(live.jobId, {
                job: {
                    jobId: live.jobId,
                    jobNumber: parseInt(String(live.jobNumber), 10) || 0,
                    jobTitle: live.jobTitle,
                    clientName: live.clientName || '',
                    isBillable: !!this.isBillable,
                },
                actions: [],
                descriptions: [],
            });
        }

        // Merge session-cached descriptions into each non-owned job. Without
        // this, a description typed/selected on a non-owned job's live entry
        // wouldn't show up in the picker until the timer stops and the entry
        // lands in recentEvents — owned jobs already pick session descs up via
        // loadUserJobDescriptions, so this brings non-owned jobs to parity.
        for (const [jobId, entry] of this.knownNonOwnedJobs) {
            const sessionList = this.sessionDescs.get(jobId);
            if (!sessionList || !sessionList.length) continue;
            const seen = new Set(entry.descriptions.map(d => d.toLowerCase()));
            const prepended: string[] = [];
            for (const d of sessionList) {
                const trimmed = (d || '').trim();
                if (!trimmed) continue;
                const key = trimmed.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                prepended.push(this.capitalizeFirst(trimmed));
                if (prepended.length + entry.descriptions.length >= this.NON_OWNED_MAX_DESCS) break;
            }
            if (prepended.length) {
                entry.descriptions = [...prepended, ...entry.descriptions]
                    .slice(0, this.NON_OWNED_MAX_DESCS);
            }
        }

        // Emit all known non-owned jobs (whether or not they're still in
        // the current recentEvents window). Prune any that are now owned.
        const out: JobWithDescriptions[] = [];
        for (const [jobId, jwd] of this.knownNonOwnedJobs) {
            if (userJobIds.has(jobId)) {
                this.knownNonOwnedJobs.delete(jobId);
                continue;
            }
            // Attach the job's actions (fetched by loadNonOwnedJobActions) so
            // its expand chevron + list appear once they've loaded.
            jwd.actions = this.jobActionsCache.get(jobId) || [];
            out.push(jwd);
        }
        return out;
    }

    /** Builds a `jobId → latest startTime ms` map from `recentEvents`.
     *  `recentEvents` is sorted newest-first by startTime, so the first
     *  occurrence per jobId is the latest entry for that job. */
    private getJobLatestTrackedMap(): Map<number, number> {
        const map = new Map<number, number>();
        for (const ev of this.recentEvents) {
            if (!ev.jobId || map.has(ev.jobId)) continue;
            const t = ev.startTime ? new Date(ev.startTime).getTime() : 0;
            map.set(ev.jobId, t);
        }
        return map;
    }

    /** Stable sort of a list by latest-tracked-time DESC. Items with no
     *  tracked time keep their relative order at the bottom of the list. */
    private sortJobsByLatestTracked<T>(items: T[], getJobId: (item: T) => number): T[] {
        const map = this.getJobLatestTrackedMap();
        return [...items].sort((a, b) => {
            const ta = map.get(getJobId(a)) || 0;
            const tb = map.get(getJobId(b)) || 0;
            return tb - ta;
        });
    }

    private rebuildJobPickerLists() {
        // Normalised, token-based matching (see normalizeSearchText) so dashes,
        // extra spaces and casing don't matter when re-filtering the search-API
        // results locally.
        const normTerm = normalizeSearchText(this.jobPickerSearch);
        // Exclude jobs already shown in the unified "My Jobs" list
        // (filteredUserJobsWithDescs) — both owned jobs and non-owned
        // recently-tracked ones — so a job there isn't duplicated in
        // "Other jobs".
        const myJobIds = new Set<number>(this.userJobs.map(j => j.jobId));
        for (const id of this.knownNonOwnedJobs.keys()) { myJobIds.add(id); }
        // Recently-tracked non-owned jobs are now merged into the unified
        // filteredUserJobsWithDescs list (see filterUserJobsWithDescs), so
        // the standalone "Previously tracked" section is empty.
        this.jobPickerGroupedEvents = [];

        const jobMap = new Map<string, Job[]>();
        // "Other jobs" list — match job number, title, or client name (both the
        // internal/display name people usually type and the legal name).
        const filteredJobs = normTerm
            ? this.jobs.filter(j => haystackMatchesTerm(
                [String(j.jobNumber || ''), j.jobTitle || '',
                 j.client?.displayName || '', j.client?.clientName || ''].join(' '),
                normTerm))
            : this.jobs;
        for (const job of filteredJobs) {
            if (myJobIds.has(job.jobId)) continue;
            // Group under the client's internal/display name (matching the
            // My Jobs rows), falling back to the legal name.
            const key = job.client?.displayName || job.client?.clientName || 'Unknown';
            if (!jobMap.has(key)) { jobMap.set(key, []); }
            jobMap.get(key).push(job);
        }
        this.jobPickerGroupedJobs = Array.from(jobMap.entries())
            .map(([clientName, jobs]) => ({
                clientName,
                jobs: jobs.sort((a, b) => (a.archive ? 1 : 0) - (b.archive ? 1 : 0)),
            }));

        // Load actions for the (live) search-result jobs so they list their
        // live actions too (chevron + list), matching the My Jobs rows.
        // Skip archived jobs — those aren't live work. Guarded/cached, so each
        // job is fetched at most once.
        for (const group of this.jobPickerGroupedJobs) {
            for (const job of group.jobs) {
                if (!job.archive) { this.loadJobActions(job.jobId); }
            }
        }
    }

    /** Start a timer on a searched job using one of its actions — the action's
     *  title becomes the entry description. Mirrors selectUserJobWithAction for
     *  the search-result rows (which carry a `Job`, not a `UserJob`). */
    selectJobWithAction(job: Job, action: JobAction) {
        this.startOrSkip({
            jobId: job.jobId,
            jobNumber: job.jobNumber,
            jobTitle: job.jobTitle,
            clientName: job.client?.displayName || job.client?.clientName || '',
            description: this.capitalizeFirst(action.title || ''),
            isBillable: !!job.isBillable,
        });
    }

    selectJobFromEvent(evt: RecentTimerEvent) {
        this.startOrSkip({
            jobId: evt.jobId,
            jobNumber: evt.jobNumber,
            jobTitle: evt.jobTitle,
            clientName: evt.clientName,
            description: evt.description || '',
            isBillable: evt.isBillable,
        });
    }

    selectJobFromJob(job: Job) {
        this.startOrSkip({
            jobId: job.jobId,
            jobNumber: job.jobNumber,
            jobTitle: job.jobTitle,
            clientName: job.client?.displayName || job.client?.clientName || '',
            // Picking just a job (no explicit action/description) should not
            // carry the previous job's description across — switching jobs is
            // a fresh context. selectJobFromEvent / selectUserJobWithAction still
            // pass an explicit description when the user chose one.
            description: '',
            isBillable: !!job.isBillable,
        });
    }

    toggleJobDescriptions(jobId: number, event: MouseEvent): void {
        event.stopPropagation();
        if (this.expandedJobIds.has(jobId)) {
            this.expandedJobIds.delete(jobId);
        } else {
            this.expandedJobIds.add(jobId);
        }
    }

    selectUserJob(uj: UserJob) {
        this.startOrSkip({
            jobId: uj.jobId,
            jobNumber: uj.jobNumber,
            jobTitle: uj.jobTitle,
            clientName: uj.clientName,
            // Picking just a user job (no explicit action) should not carry the
            // previous job's description across — switching jobs is a fresh
            // context. selectUserJobWithAction still passes the chosen action's
            // title as the description when the user picked one.
            description: '',
            isBillable: !!uj.isBillable,
        });
    }

    /** Picks a job from the dropdown: starts a fresh entry via continueEntry,
     *  but bails out (no-op) when the requested job+description matches the
     *  one already running — clicking the same row shouldn't disturb a live
     *  timer. If the same job is running but the requested description differs,
     *  update the live entry's description in place rather than restarting. */
    private startOrSkip(data: ContinueEntryData): void {
        if (this.isCurrentEntry(data.jobId, data.description || '')) {
            this.closeJobPicker();
            return;
        }
        // Same job, different description: update the running entry's description
        // in place. Empty description means the user picked the job header alone
        // (not a specific entry) — treat that as a no-op rather than wiping the
        // live entry's existing description.
        if (this.timerEntryService.isLiveTimerRunning &&
            this.selectedJob?.jobId === data.jobId) {
            const desc = (data.description || '').trim();
            if (desc) {
                this.description = this.capitalizeFirst(desc);
                this.saveDescription();
            }
            this.closeJobPicker();
            return;
        }
        this.timerEntryService.continueEntry(data);
        this.closeJobPicker();
    }

    private isCurrentEntry(jobId: number, description: string): boolean {
        if (!this.timerEntryService.isLiveTimerRunning) return false;
        if (this.selectedJob?.jobId !== jobId) return false;
        // The header capitalizes the description's first letter, so compare
        // after trimming + lowercasing both sides to avoid false-misses.
        const current = (this.description || '').trim().toLowerCase();
        const next = (description || '').trim().toLowerCase();
        return current === next;
    }

    /** Restart the timer using the currently selected job (shown in the pill). */
    restartLastTimer() {
        if (!this.selectedJob || this.timerStartTime) return;
        const job = this.selectedJob;
        this.startLocalTimer();
        this.timerEntryService.runningTimerSaveInFlight++;
        this.timeEventService.startTimer({
            jobId: job.jobId,
            description: this.description || '',
        }).subscribe({
            next: res => {
                if (res.success && res.runningEvent) {
                    this.isBillable = res.runningEvent.isBillable;
                    this.timerEntryService.updateLiveEntryId(res.runningEvent.timeEventId);
                    this.timerEntryService.updateLiveJob({ ...job, isBillable: this.isBillable });
                    this.cdr.markForCheck();
                }
                this.loadRecentEvents();
            },
            complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
            error: () => {
                this.timerEntryService.runningTimerSaveInFlight--;
                // The server never created the running timer (network
                // failure or 5xx). Roll the visual timer back so the UI
                // matches the (real) "no timer running" server state —
                // otherwise it would keep ticking against a non-existent
                // backend entry and any work the user does next is lost.
                this.revertStartedTimer();
                this.toastService.show('Could not start the timer — please try again.');
            },
        });
    }

    startFromUserJob(uj: UserJob) {
        if (this.timerEntryService.isLiveTimerRunning) {
            this.isStopping = true;
            this.timerEntryService.stopEntry();
            this.isStopping = false;
        }
        this.selectedJob = {
            jobId: uj.jobId,
            jobNumber: uj.jobNumber,
            jobTitle: uj.jobTitle,
            clientName: uj.clientName
        };
        this.closeDropdown();
        this.startLocalTimer();
        this.timerEntryService.runningTimerSaveInFlight++;
        this.timeEventService.startTimer({
            jobId: uj.jobId,
            description: this.description || '',
        }).subscribe({
            next: res => {
                if (res.success && res.runningEvent) {
                    this.isBillable = res.runningEvent.isBillable;
                    this.timerEntryService.updateLiveEntryId(res.runningEvent.timeEventId);
                    this.timerEntryService.updateLiveJob({ ...this.selectedJob!, isBillable: this.isBillable });
                    this.cdr.markForCheck();
                }
                this.loadRecentEvents();
            },
            complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
            error: () => {
                this.timerEntryService.runningTimerSaveInFlight--;
                // The server never created the running timer (network
                // failure or 5xx). Roll the visual timer back so the UI
                // matches the (real) "no timer running" server state —
                // otherwise it would keep ticking against a non-existent
                // backend entry and any work the user does next is lost.
                this.revertStartedTimer();
                this.toastService.show('Could not start the timer — please try again.');
            },
        });
    }

    /** Start a timer on a job using one of its actions — the action's title
     *  becomes the entry description. Mirrors selecting a job alone, but
     *  carries the chosen action across as the description. */
    selectUserJobWithAction(uj: UserJob, action: JobAction) {
        this.startOrSkip({
            jobId: uj.jobId,
            jobNumber: uj.jobNumber,
            jobTitle: uj.jobTitle,
            clientName: uj.clientName,
            description: this.capitalizeFirst(action.title || ''),
            isBillable: !!uj.isBillable,
        });
    }

    // ── Actions ──────────────────────────────────────────────────────────────────

    startFromEvent(evt: RecentTimerEvent) {
        if (this.timerEntryService.isLiveTimerRunning) {
            this.isStopping = true;
            this.timerEntryService.stopEntry();
            this.isStopping = false;
        }
        this.selectedJob = {
            jobId: evt.jobId,
            jobNumber: evt.jobNumber,
            jobTitle: evt.jobTitle,
            clientName: evt.clientName
        };
        this.closeDropdown();
        this.startLocalTimer();
        this.timerEntryService.runningTimerSaveInFlight++;
        this.timeEventService.startTimer({
            jobId: evt.jobId,
            description: evt.description || '',
        }).subscribe({
            next: res => {
                if (res.success && res.runningEvent) {
                    this.isBillable = res.runningEvent.isBillable;
                    this.timerEntryService.updateLiveEntryId(res.runningEvent.timeEventId);
                    this.timerEntryService.updateLiveJob({ ...this.selectedJob!, isBillable: this.isBillable });
                    this.cdr.markForCheck();
                }
                this.loadRecentEvents();
            },
            complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
            error: () => {
                this.timerEntryService.runningTimerSaveInFlight--;
                // The server never created the running timer (network
                // failure or 5xx). Roll the visual timer back so the UI
                // matches the (real) "no timer running" server state —
                // otherwise it would keep ticking against a non-existent
                // backend entry and any work the user does next is lost.
                this.revertStartedTimer();
                this.toastService.show('Could not start the timer — please try again.');
            },
        });
    }

    startFromJob(job: Job) {
        if (this.timerEntryService.isLiveTimerRunning) {
            this.isStopping = true;
            this.timerEntryService.stopEntry();
            this.isStopping = false;
        }
        this.selectedJob = {
            jobId: job.jobId,
            jobNumber: job.jobNumber,
            jobTitle: job.jobTitle,
            clientName: job.client?.displayName || job.client?.clientName || ''
        };
        this.isBillable = !!job.isBillable;
        this.closeDropdown();
        this.startLocalTimer();
        this.timerEntryService.runningTimerSaveInFlight++;
        this.timeEventService.startTimer({
            jobId: job.jobId,
            description: this.description || '',
            isBillable: !!job.isBillable,
        }).subscribe({
            next: res => {
                if (res.success && res.runningEvent) {
                    this.timerEntryService.updateLiveEntryId(res.runningEvent.timeEventId);
                }
                this.loadRecentEvents();
            },
            complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
            error: () => {
                this.timerEntryService.runningTimerSaveInFlight--;
                // The server never created the running timer (network
                // failure or 5xx). Roll the visual timer back so the UI
                // matches the (real) "no timer running" server state —
                // otherwise it would keep ticking against a non-existent
                // backend entry and any work the user does next is lost.
                this.revertStartedTimer();
                this.toastService.show('Could not start the timer — please try again.');
            },
        });
    }

    private isStopping = false;

    stopTimer() {
        // De-duplicate: if a previous stopTimer is still in flight, don't
        // fire another one. With the pessimistic-stop pattern below the
        // user keeps seeing the running timer until the first attempt
        // resolves, so a second click would otherwise queue another API
        // call against the same already-stopping server-side entry.
        if (this.timerEntryService.runningTimerSaveInFlight > 0) return;

        // Pessimistic stop: keep the visual timer running until the server
        // confirms the stop. Without this, an offline / 5xx stop would
        // produce the worst possible UX — the user sees the timer
        // "stopped" and an entry added to the table, then on reload the
        // entry vanishes and the timer comes back (because the server
        // still has it running). Now we only commit the local stop after
        // the server has actually persisted it.
        // Immediate feedback: freeze the visible counter and show the button
        // as busy the instant Stop is pressed, so the press registers right
        // away. This is purely visual — the actual stop stays pessimistic and
        // is only committed below once the server confirms.
        this.stopping = true;
        this.updateTimerDisplay();

        this.timerEntryService.runningTimerSaveInFlight++;
        this.timeEventService.stopTimer().subscribe({
            next: () => {
                // Server confirmed the stop — NOW do the local stop. Same
                // sequence the original optimistic flow used, just moved
                // inside the success branch.
                this.lastLocalMutationAt = Date.now();
                this.isStopping = true;
                this.stopping = false;
                this.timerEntryService.stopEntry();
                this.resetToDefault();
                this.isStopping = false;
                this.loadRecentEvents();
                this.loadUserJobDescriptions();
            },
            complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
            error: () => {
                this.timerEntryService.runningTimerSaveInFlight--;
                // The server didn't accept the stop — the timer is still
                // running there, and we never touched the local state so
                // it's still running here too. Unfreeze the counter (it
                // resumes from the real elapsed time), inform the user, and
                // let them try again when conditions improve.
                this.stopping = false;
                this.updateTimerDisplay();
                this.cdr.markForCheck();
                this.toastService.show('Could not stop the timer — please try again.');
            },
        });
    }

    /** Roll back a local timer that we just started visually but whose
     *  server-side start failed (typically: offline). Mirrors what
     *  `stopTimer()` does locally but without making the API call —
     *  the server never recorded a running timer, so there's nothing
     *  to stop on it. Without this, the visual timer would keep
     *  ticking even though the entry doesn't exist on the server, and
     *  any work the user does next would be lost on reload. */
    private revertStartedTimer(): void {
        this.lastLocalMutationAt = Date.now();
        // Preserve the user's typed description through the revert so they
        // can simply click play again to retry without re-typing. The
        // selectedJob and isBillable are preserved by `resetToDefault()`
        // already (see comment at the top of that method); description
        // is the one user-input field it clears, so we restore it after.
        const typedDescription = this.description;
        this.isStopping = true;
        this.timerEntryService.stopEntry();
        this.resetToDefault();
        this.isStopping = false;
        if (typedDescription) {
            this.description = typedDescription;
        }
        this.cdr.markForCheck();
    }

    private startLocalTimer() {
        // Mark a local mutation so cross-window poll-ticks don't reconcile
        // against the in-flight start request and revert UI to the previous
        // job for a few seconds.
        this.lastLocalMutationAt = Date.now();
        this.timerStartTime = new Date();
        // New local timer: drop any previous server-start baseline so the first
        // poll re-baselines against this timer and keeps our local anchor.
        this.lastSeenRemoteStartMs = null;
        this.notifyCalendar();
        // Refresh the visible counter synchronously so it snaps to 0:00:00
        // the instant the user switches jobs. Without this, `timerDisplay`
        // still holds the previous timer's elapsed-time string until the
        // 1-second tickInterval fires next, which the user perceives as
        // the old timer "lingering" for up to a second before the new one
        // starts counting.
        this.updateTimerDisplay();
        // Re-align the 1Hz ticker to the click. The interval set up in
        // ngOnInit fires at a phase determined by when the component
        // mounted, not by when each new timer starts. Without this realign,
        // clicking just after a tick means the next tick fires <1s later
        // (still elapsed = 0 → "0:00:00") and the SECOND tick after that
        // — up to ~2s after the click — is the first one that shows
        // "0:00:01". Resetting the interval here makes the next tick fire
        // ~1000ms after the click, so the first second appears on time.
        this.scheduleTick();
    }

    private notifyCalendar() {
        if (!this.selectedJob || !this.timerStartTime) return;
        this.timerEntryService.addEntry({
            jobId: this.selectedJob.jobId,
            jobNumber: this.selectedJob.jobNumber,
            jobTitle: this.selectedJob.jobTitle,
            clientName: this.selectedJob.clientName,
            description: this.description || '',
            start: this.timerStartTime,
            isBillable: this.isBillable
        });
    }

    confirmLongTimer(): void {
        // Local dismissal — clears the alert and advances `nextAlertAtMs`
        // past the current threshold window. Emits `longTimerDismissed$`,
        // which `TimerSyncService` broadcasts as 'long-timer-dismissed'
        // for same-browser sibling windows.
        this.timerEntryService.dismissLongTimerAlert();
        // Cross-process sync (browser tab ↔ desktop app): record the
        // dismissal on the server so the other window picks it up on its
        // next `getRunningTimer` poll.
        this.timeEventService.dismissLongTimer().subscribe({
            error: err => console.error('[timer-header] dismissLongTimer failed:', err),
        });
        this.lastDismissedAtAppliedMs = Date.now();
    }

    stopLongTimer(): void {
        // Mirror the in-modal dismissal so other same-browser windows
        // clear their copy of the alert instantly. The stop itself
        // already broadcasts 'timer-changed' which clears the alert via
        // the local `stopEntry()` path, but a same-browser sibling will
        // see this faster than the server roundtrip.
        this.timerEntryService.dismissLongTimerAlert();
        this.stopTimer();
    }

    /** Last `longTimerDismissedAt` we've already applied. Tracked so
     *  `applyRemoteTimerState` doesn't re-apply the same server-side
     *  dismissal on every poll-tick. */
    private lastDismissedAtAppliedMs = 0;

    saveDescription() {
        const trimmed = this.description.trim();
        // Clear action selection if description no longer matches
        if (this.selectedActionId != null) {
            const action = this.jobActions.find(a => a.jobActionId === this.selectedActionId);
            const actionTitle = this.capitalizeFirst(action?.title || '');
            if (trimmed !== actionTitle) {
                this.selectedActionId = null;
            }
        }
        this.isSendingDesc = true;
        this.timerEntryService.updateDescription(trimmed);
        this.isSendingDesc = false;
        // Track description in session cache (most recent first)
        if (trimmed && this.selectedJob) {
            this.addToSessionDescs(this.selectedJob.jobId, trimmed);
        }
        if (this.timerEntryService.isLiveTimerRunning) {
            this.timeEventService.updateRunningTimer({ description: trimmed }).subscribe({
                next: () => { this.loadUserJobDescriptions(); },
                // Without this handler an offline description edit used to be
                // silently swallowed — the header showed the new description
                // but the server kept the old one.
                error: () => this.toastService.show('Could not save the timer — please try again.'),
            });
        }
    }

    /** Adds a description to the session cache for a job. Delegates to
     *  the shared `RecentDescriptionsService` so the cache stays in
     *  sync across components (the timer-tracker writes through the
     *  same service for list-view / modal description edits). The
     *  local `sessionDescs` mirror is then refreshed from the service
     *  so subsequent reads inside this component pick up the change
     *  without a localStorage round-trip per access. */
    private addToSessionDescs(jobId: number, desc: string) {
        this.recentDescriptionsService.add(jobId, desc);
        this.sessionDescs = this.recentDescriptionsService.getAll();
    }

    /** Loads the session descriptions cache from the shared service
     *  (which reads localStorage). Re-called whenever a sync event
     *  fires so writes from other components (e.g. list-view inline
     *  description edits in timer-tracker) get picked up immediately. */
    private loadSessionDescs() {
        this.sessionDescs = this.recentDescriptionsService.getAll();
    }

    /** Persists the session descriptions cache to localStorage. */
    private persistSessionDescs() {
        try {
            const entries = Array.from(this.sessionDescs.entries());
            localStorage.setItem(TimerHeaderComponent.SESSION_DESCS_KEY, JSON.stringify(entries));
        } catch { /* storage full or unavailable */ }
    }

    /** Loads the last selected job from localStorage (pill persistence). */
    private loadLastJob() {
        try {
            const raw = localStorage.getItem(TimerHeaderComponent.LAST_JOB_KEY);
            if (!raw) return;
            const data = JSON.parse(raw);
            if (data && typeof data.jobId === 'number') {
                this.selectedJob = {
                    jobId: data.jobId,
                    jobNumber: data.jobNumber,
                    jobTitle: data.jobTitle,
                    clientName: data.clientName,
                    isArchived: !!data.isArchived,
                };
                this.isBillable = !!data.isBillable;
                // Re-validate archive status against the server — the job may
                // have been archived since it was last selected.
                this.jobService.getJob(data.jobId).subscribe({
                    next: job => {
                        if (this.selectedJob?.jobId === data.jobId) {
                            this.selectedJob = { ...this.selectedJob, isArchived: !!job.archive };
                            this.persistLastJob();
                            this.cdr.markForCheck();
                        }
                    },
                    error: () => { /* leave persisted value as-is if the request fails */ },
                });
            }
        } catch { /* ignore corrupted data */ }
    }

    /** Persists the currently selected job (or clears it if null). */
    private persistLastJob() {
        try {
            if (this.selectedJob) {
                localStorage.setItem(TimerHeaderComponent.LAST_JOB_KEY, JSON.stringify({
                    ...this.selectedJob,
                    isBillable: this.isBillable,
                    isArchived: this.selectedJob.isArchived ?? false,
                }));
            } else {
                localStorage.removeItem(TimerHeaderComponent.LAST_JOB_KEY);
            }
        } catch { /* storage full or unavailable */ }
    }

    // ── Description dropdown (actions for selected job) ──────────────────────

    openDescDropdown() {
        if (this.descDropdownOpen) { return; }
        if (this.dropdownOpen) { this.closeDropdown(); }
        if (this.jobPickerOpen) { this.closeJobPicker(); }
        if (!this.selectedJob) { return; }
        this.descDropdownOpen = true;
        this.descSearch = '';
        // Refresh the session-desc cache so a description just typed elsewhere
        // in THIS window (e.g. the timer-tracker list) is available right away
        // — the BroadcastChannel sync doesn't echo back to the same window.
        this.loadSessionDescs();
        const jobChanged = this._descJobId !== this.selectedJob.jobId;
        // If same job and already loaded, show cached data immediately (but
        // rebuild the descriptions from the fresh session cache so a
        // just-typed description still appears).
        if (!jobChanged && this.jobActions.length) {
            this.filteredActions = this.jobActions;
            this.splitActionSections(this.filteredActions);
            this.rebuildDescOptions();
            return;
        }
        // New job or no data — clear stale data and show spinner
        this.jobActions = [];
        this.filteredActions = [];
        this.myActions = [];
        this.waitingActions = [];
        this.otherActions = [];
        this.descOptions = [];
        this.filteredDescOptions = [];
        this._descRecentDescs = [];
        this.isActionsLoading = true;
        this._descJobId = this.selectedJob.jobId;
        const descJobId = this.selectedJob.jobId;
        // Actions load exactly as before (unchanged behaviour). Session-cached
        // descriptions already show via rebuildDescOptions once actions arrive.
        this.actionService.fetchAllForJob(descJobId).subscribe({
            next: actions => {
                this.jobActions = actions.filter(a => a.actionStatus !== ActionStatus.Completed && a.actionStatus !== ActionStatus.Archived);
                this.filteredActions = this.descSearch
                    ? this.jobActions.filter(a => (a.title || '').toLowerCase().includes(this.descSearch.toLowerCase()))
                    : this.jobActions;
                this.splitActionSections(this.filteredActions);
                this.rebuildDescOptions();
                this.isActionsLoading = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.isActionsLoading = false;
                this.cdr.markForCheck();
            }
        });
        // Recent entry descriptions load independently (best-effort) so a
        // failure here never blocks the actions list. Guarded against the job
        // changing before this resolves.
        this.timeEventService.getRecentDescriptions(descJobId, 10).subscribe({
            next: recent => {
                if (this._descJobId !== descJobId) { return; }
                this._descRecentDescs = recent?.descriptions || [];
                this.rebuildDescOptions();
                this.cdr.markForCheck();
            },
            error: () => { /* keep session-only descriptions */ },
        });
        // Keep focus on the desc-input so the user can type
        setTimeout(() => {
            const descInput = this.eRef.nativeElement.querySelector('.thn__desc-input');
            descInput?.focus();
        });
    }

    closeDescDropdown() {
        this.descDropdownOpen = false;
        this.descSearch = '';
    }

    onDescSearch() {
        const term = this.descSearch.toLowerCase();
        this.filteredActions = term
            ? this.jobActions.filter(a => (a.title || '').toLowerCase().includes(term))
            : this.jobActions;
        this.splitActionSections(this.filteredActions);
        this.filteredDescOptions = this.filterDescOptions(this.descSearch);
    }

    selectAction(action: JobAction) {
        this.selectedActionId = action.jobActionId || null;
        this.description = this.capitalizeFirst(action.title || '');
        this.closeDescDropdown();
        this.saveDescription();
    }

    /** Start/set a custom (typed) description from the "Recent descriptions"
     *  section. Not tied to an action, so clear any selected action. */
    selectDescription(desc: string) {
        this.selectedActionId = null;
        this.description = this.capitalizeFirst(desc || '');
        this.closeDescDropdown();
        this.saveDescription();
    }

    /** True when a listed description matches the current entry's description
     *  (drives the tick), compared case-insensitively. */
    isDescriptionSelected(desc: string): boolean {
        return (this.description || '').trim().toLowerCase() === (desc || '').trim().toLowerCase();
    }

    /** Rebuilds the custom-descriptions list for the desc dropdown from the
     *  session cache (typed descriptions, including ones just typed) plus the
     *  backend's recent descriptions, excluding any that duplicate an action
     *  title (those already show in the action sections). Deduped, meaningful
     *  text only. */
    private rebuildDescOptions() {
        const jobId = this.selectedJob?.jobId;
        if (jobId == null) { this.descOptions = []; this.filteredDescOptions = []; return; }
        const actionTitles = new Set(
            this.jobActions.map(a => (a.title || '').trim().toLowerCase()).filter(Boolean)
        );
        const isMeaningful = (s: string) => !!s && !/^[\s\-–—]+$/.test(s);
        const seen = new Set<string>();
        const out: string[] = [];
        const session = this.sessionDescs.get(jobId) || [];
        for (const raw of [...session, ...this._descRecentDescs]) {
            const desc = this.capitalizeFirst((raw || '').trim());
            const low = desc.toLowerCase();
            if (isMeaningful(desc) && !actionTitles.has(low) && !seen.has(low)) {
                seen.add(low);
                out.push(desc);
            }
        }
        this.descOptions = out;
        this.filteredDescOptions = this.filterDescOptions(this.descSearch);
    }

    private filterDescOptions(term: string): string[] {
        const t = (term || '').toLowerCase();
        return t ? this.descOptions.filter(d => d.toLowerCase().includes(t)) : this.descOptions;
    }

    private capitalizeFirst(text: string): string {
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
    }

    private splitActionSections(actions: JobAction[]) {
        const uid = this.loginService.userId;
        this.myActions = [];
        this.waitingActions = [];
        this.otherActions = [];
        for (const a of actions) {
            const isMyAction = (a.assignees && a.assignees.some(au => au.user?.id === uid))
                || (a.assignedUsers && a.assignedUsers.includes(uid));
            const isWaitingForMe = a.waitingUserId && a.waitingUserId === uid;
            if (isMyAction) {
                this.myActions.push(a);
            } else if (isWaitingForMe) {
                this.waitingActions.push(a);
            } else {
                this.otherActions.push(a);
            }
        }
    }

    private _descJobId: number | null = null;

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        if (this.dropdownOpen && !this.eRef.nativeElement.contains(event.target as Node)) {
            this.closeDropdown();
        }
        if (this.jobPickerOpen && !this.eRef.nativeElement.contains(event.target as Node)) {
            this.closeJobPicker();
        }
        if (this.descDropdownOpen && !this.eRef.nativeElement.contains(event.target as Node)) {
            this.closeDescDropdown();
        }
        if (this.showStartEditPopover && !this.eRef.nativeElement.contains(event.target as Node)) {
            this.closeStartEditPopover();
        }
    }

    @HostListener('document:mousedown', ['$event'])
    onMouseDown(event: Event) {
        const active = document.activeElement as HTMLElement;
        if (active?.classList?.contains('thn__desc-input') && !active.contains(event.target as Node)) {
            active.blur();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.showStartEditPopover) this.closeStartEditPopover();
    }

    // ─── Start-time edit popover ─────────────────────────────────────────────────

    /** Toggle / open the popover. Called from a click on the timer display.
     *  Only active while a timer is running — clicking the inert "0:00:00"
     *  when idle is a no-op. */
    openStartEditPopover(event: MouseEvent): void {
        if (!this.timerEntryService.isLiveTimerRunning) return;
        event.stopPropagation();
        if (this.showStartEditPopover) {
            this.closeStartEditPopover();
            return;
        }
        const start = this.timerEntryService.liveStartTime || this.timerStartTime;
        if (!start) return;
        this.editStartTime = this.formatTimeForInput(start);
        // End for a live entry is "now" — frozen for display purposes. The
        // user can't edit it, so we don't need to keep it ticking inside the
        // popover; refreshing it on each open is enough.
        this.editEndTime = this.formatTimeForInput(new Date());
        this.initialStartTimeStr = this.editStartTime;
        this.timeBuf = '';
        this.activeTimeSeg = null;
        this.timeValidationError = false;
        this.timeValidationMessage = '';
        this.showStartEditPopover = true;
        // Focus the start-h segment so the user can immediately type.
        setTimeout(() => {
            const el = this.eRef.nativeElement.querySelector(
                '.thn__start-edit-popover [data-seg-id="start-h"]'
            ) as HTMLInputElement | null;
            el?.focus();
            el?.select?.();
        }, 0);
    }

    closeStartEditPopover(): void {
        this.showStartEditPopover = false;
        this.activeTimeSeg = null;
        this.timeBuf = '';
    }

    /** Save the edited start time. Mirrors the calendar modal's live-entry
     *  save branch: local state first, then the dedicated start-time endpoint,
     *  then the cross-window sync notify. */
    saveStartEdit(): void {
        if (!this.timerEntryService.isLiveTimerRunning) {
            this.closeStartEditPopover();
            return;
        }
        this.validateStartEditTime();
        if (this.timeValidationError) return;
        if (this.editStartTime === this.initialStartTimeStr) {
            // No-op: nothing changed.
            this.closeStartEditPopover();
            return;
        }

        const [sh, sm] = this.editStartTime.split(':').map(Number);
        if (isNaN(sh) || isNaN(sm)) return;

        // Preserve the original date of the running timer — only the time
        // portion is editable from the header. Overnight-running timers (e.g.
        // started yesterday at 23:00) stay anchored to their original day.
        const base = this.timerEntryService.liveStartTime || this.timerStartTime || new Date();
        const newStart = new Date(base);
        newStart.setHours(sh, sm, 0, 0);

        // Block poll-tick reconciliation while the PUT is in flight — same
        // guard the start/stop flows use — so a stale "running timer" fetch
        // can't revert the just-updated UI.
        this.lastLocalMutationAt = Date.now();
        this.timerEntryService.lastLocalMutationAt = Date.now();

        // Capture the pre-edit start time so we can revert the visible
        // timer (and the shared service state) if the save fails. Without
        // this the new start time would keep ticking on screen even though
        // the server still had the old one — giving the false impression
        // that the change was saved.
        const previousStart = this.timerEntryService.liveStartTime || this.timerStartTime;

        // Local first so the display ticks against the new start immediately.
        this.timerStartTime = newStart;
        this.timerEntryService.updateStartTime(newStart);
        this.updateTimerDisplay();

        this.timerEntryService.runningTimerSaveInFlight++;
        this.timeEventService.updateRunningTimerStartTime(
            TimeEventService.toIso(newStart)
        ).subscribe({
            next: () => {
                this.lastLocalMutationAt = Date.now();
                this.timerEntryService.lastLocalMutationAt = Date.now();
                this.timerSyncService.notify('entries-changed');
            },
            complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
            error: () => {
                this.timerEntryService.runningTimerSaveInFlight--;
                // Revert the local start time so the visible timer matches
                // what the server still believes — otherwise the user sees
                // the new start tick on screen, thinks it saved, and loses
                // the edit on next reload.
                if (previousStart) {
                    this.timerStartTime = previousStart;
                    this.timerEntryService.updateStartTime(previousStart);
                    this.updateTimerDisplay();
                    this.cdr.markForCheck();
                }
                this.toastService.show('Could not save the change — the timer was reverted.');
            },
        });

        this.closeStartEditPopover();
        this.cdr.markForCheck();
    }

    /** Validate the start-time field. Live entries cannot have a start time
     *  in the future (end is locked to "now", so a future start would create
     *  a negative-duration entry). Mirrors `validateTimeInputs`'s live branch. */
    private validateStartEditTime(): void {
        const [sh, sm] = (this.editStartTime || '').split(':').map(Number);
        if (isNaN(sh) || isNaN(sm)) {
            this.timeValidationError = false;
            this.timeValidationMessage = '';
            return;
        }
        const base = this.timerEntryService.liveStartTime || this.timerStartTime || new Date();
        const candidate = new Date(base);
        candidate.setHours(sh, sm, 0, 0);
        if (candidate.getTime() > Date.now()) {
            this.timeValidationError = true;
            this.timeValidationMessage = 'Start time cannot be in the future.';
        } else {
            this.timeValidationError = false;
            this.timeValidationMessage = '';
        }
    }

    // ─── Segmented time picker helpers ──────────────────────────────────────────
    //
    // Ported from timer-tracker.component (the calendar modal's picker) so
    // both pickers behave identically — Up/Down to nudge, type digits to set
    // (24-hour), Tab to advance. Kept local rather than extracted so this
    // addition doesn't require a wider refactor.

    private formatTimeForInput(date: Date): string {
        const hours = date.getHours().toString().padStart(2, '0');
        const mins = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${mins}`;
    }

    private parseHhmm(hhmm: string): { h24: number; m: number } {
        const parts = (hhmm || '00:00').split(':').map(Number);
        return { h24: parts[0] ?? 0, m: parts[1] ?? 0 };
    }

    private getStartEditTimeStr(field: 'start' | 'end'): string {
        return field === 'start' ? (this.editStartTime || '00:00') : (this.editEndTime || '00:00');
    }

    getSegDisplay(segId: string): string {
        const field = segId.startsWith('start') ? 'start' : 'end';
        const { h24, m } = this.parseHhmm(this.getStartEditTimeStr(field));
        // While actively editing, show the raw buffer (may be empty after
        // Backspace) instead of the committed value.
        const editing = this.activeTimeSeg === segId && this.timeBufDirty;
        if (segId.endsWith('-h')) {
            // 24-hour, zero-padded.
            return editing ? this.timeBuf : h24.toString().padStart(2, '0');
        }
        if (segId.endsWith('-m')) {
            return editing ? this.timeBuf : m.toString().padStart(2, '0');
        }
        return '';
    }

    onSegFocus(segId: string): void {
        this.activeTimeSeg = segId;
        this.timeBuf = '';
        this.timeBufDirty = false;
        this.timeSuppressAdvance = false;
    }

    onSegBlur(segId: string): void {
        if (this.timeBuf) {
            const field = segId.startsWith('start') ? 'start' : 'end';
            const isHours = segId.endsWith('-h');
            const val = Math.max(0, Math.min(isHours ? 23 : 59, parseInt(this.timeBuf)));
            this.setTimePart(field, isHours ? 'h' : 'm', val);
        }
        this.timeBuf = '';
        this.timeBufDirty = false;
        this.activeTimeSeg = null;
    }

    onSegKeydown(e: KeyboardEvent, segId: string): void {
        if (e.key === 'Tab') return;
        if (e.key === 'Enter') {
            e.preventDefault();
            this.saveStartEdit();
            return;
        }

        // Caret-aware Left/Right: move within the segment, cross at the edge.
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            this.handleSegArrow(e, segId);
            return;
        }

        e.preventDefault();

        const input = e.target as HTMLInputElement;
        const caret = input.selectionStart ?? (this.timeBufDirty ? this.timeBuf.length : input.value.length);

        if (/^\d$/.test(e.key)) {
            this.insertSegDigit(segId, e.key, caret);
            return;
        }

        if (e.key === 'ArrowUp')   { this.timeBuf = ''; this.timeBufDirty = false; this.adjustSeg(segId,  1); return; }
        if (e.key === 'ArrowDown') { this.timeBuf = ''; this.timeBufDirty = false; this.adjustSeg(segId, -1); return; }

        // Backspace deletes the digit before the caret (seeding from the shown
        // value), keeping the caret put. Delete clears the whole segment. Both
        // suppress auto-advance so editing in place doesn't jump fields.
        if (e.key === 'Backspace') {
            this.timeSuppressAdvance = true;
            if (!this.timeBufDirty) {
                this.timeBuf = this.committedSegStr(segId);
                this.timeBufDirty = true;
            }
            const at = Math.min(caret, this.timeBuf.length);
            if (at > 0) {
                this.timeBuf = this.timeBuf.slice(0, at - 1) + this.timeBuf.slice(at);
                this.restoreSegCaret(segId, at - 1);
            }
            return;
        }
        if (e.key === 'Delete') {
            this.timeSuppressAdvance = true;
            this.timeBuf = '';
            this.timeBufDirty = true;
            this.restoreSegCaret(segId, 0);
            return;
        }
    }

    onSegInput(e: Event, segId: string): void {
        const input = e.target as HTMLInputElement;
        const ie = e as InputEvent;
        const inputType = ie.inputType;
        const data = ie.data;

        // Fallback for mobile keyboards, whose `keydown.key` often comes through as "Unidentified".
        if (inputType === 'insertText' && data && /^\d$/.test(data)) {
            const caret = Math.max(0, (input.selectionStart ?? this.timeBuf.length) - 1);
            this.insertSegDigit(segId, data, caret);
        }

        input.value = this.getSegDisplay(segId);
    }

    /** Shared by keydown (desktop) and the input-event fallback (mobile) below. */
    private insertSegDigit(segId: string, digit: string, caret: number): void {
        const field = segId.startsWith('start') ? 'start' : 'end';
        const isHours = segId.endsWith('-h');
        const isMin   = segId.endsWith('-m');

        // Fresh segment → first digit replaces; once editing, insert at caret.
        if (!this.timeBufDirty) {
            this.timeBuf = '';
            this.timeBufDirty = true;
        }
        const at = this.timeBuf.length === 0 ? 0 : Math.min(caret, this.timeBuf.length);
        this.timeBuf = this.timeBuf.slice(0, at) + digit + this.timeBuf.slice(at);
        this.restoreSegCaret(segId, at + 1);

        const partial = parseInt(this.timeBuf);
        const autoComplete = this.timeBuf.length >= 2
            || (isHours && partial > 2)
            || (isMin   && partial > 5);
        if (autoComplete) {
            const appendedAtEnd = at + 1 === this.timeBuf.length;
            const clamped = Math.max(0, Math.min(isHours ? 23 : 59, partial));
            this.setTimePart(field, isHours ? 'h' : 'm', clamped);
            this.timeBuf = '';
            this.timeBufDirty = false;
            if (appendedAtEnd && !this.timeSuppressAdvance) this.advanceSeg(segId);
        }
    }

    private setTimePart(field: 'start' | 'end', part: 'h' | 'm', value: number): void {
        const { h24, m } = this.parseHhmm(this.getStartEditTimeStr(field));
        const newH = part === 'h' ? value : h24;
        const newM = part === 'm' ? value : m;
        const newTime = `${newH.toString().padStart(2, '0')}:${newM.toString().padStart(2, '0')}`;
        if (field === 'start') this.editStartTime = newTime;
        else                   this.editEndTime   = newTime;
        this.validateStartEditTime();
    }

    private adjustSeg(segId: string, delta: number): void {
        const field = segId.startsWith('start') ? 'start' : 'end';
        const { h24, m } = this.parseHhmm(this.getStartEditTimeStr(field));
        if (segId.endsWith('-h')) {
            let v = h24 + delta; if (v > 23) v = 0; if (v < 0) v = 23;
            this.setTimePart(field, 'h', v);
        } else if (segId.endsWith('-m')) {
            let v = m + delta; if (v > 59) v = 0; if (v < 0) v = 59;
            this.setTimePart(field, 'm', v);
        }
    }

    private advanceSeg(currentSegId: string): void {
        // End segments are disabled in this popover — only Start segments are
        // navigated. Order omits the end-* ids to match.
        const order = ['start-h', 'start-m'];
        const next = order[order.indexOf(currentSegId) + 1];
        if (next) {
            const el = this.eRef.nativeElement.querySelector(
                `.thn__start-edit-popover [data-seg-id="${next}"]`
            ) as HTMLInputElement | null;
            if (el) el.focus();
        }
    }

    /** Segment order for the header's start-edit popover (start only — the end
     *  segments are disabled on a live entry). */
    private readonly HDR_SEG_NAV_ORDER = ['start-h', 'start-m'];

    private startSegEl(segId: string): HTMLInputElement | null {
        return this.eRef.nativeElement.querySelector(
            `.thn__start-edit-popover [data-seg-id="${segId}"]`,
        ) as HTMLInputElement | null;
    }

    /** Restore the caret in a segment after Angular re-renders its [value]
     *  (which would otherwise jump the caret to the end). */
    private restoreSegCaret(segId: string, pos: number): void {
        setTimeout(() => {
            const el = this.startSegEl(segId);
            if (el && document.activeElement === el) {
                try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
            }
        }, 0);
    }

    /** Committed (shown) value of a segment, for seeding the buffer on Backspace. */
    private committedSegStr(segId: string): string {
        const field = segId.startsWith('start') ? 'start' : 'end';
        const { h24, m } = this.parseHhmm(this.getStartEditTimeStr(field));
        return (segId.endsWith('-h') ? h24 : m).toString().padStart(2, '0');
    }

    /** Caret-aware Left/Right: move the caret within the segment natively until
     *  its edge, then cross to the neighbour (the colon counts as one position,
     *  so the minutes' first stop is caret 1). */
    private handleSegArrow(e: KeyboardEvent, segId: string): void {
        const input = e.target as HTMLInputElement;
        const len = input.value.length;
        const pos = input.selectionStart ?? len;
        const min = segId.endsWith('-h') ? 0 : 1;
        if (e.key === 'ArrowRight') {
            if (pos < len) return;
            e.preventDefault();
            this.crossSeg(segId, 1);
        } else {
            if (pos > min) return;
            e.preventDefault();
            this.crossSeg(segId, -1);
        }
    }

    private crossSeg(segId: string, dir: 1 | -1): void {
        const order = this.HDR_SEG_NAV_ORDER;
        const idx = order.indexOf(segId);
        if (idx < 0) return;
        const t = idx + dir;
        if (t < 0 || t >= order.length) return;
        const el = this.startSegEl(order[t]);
        if (!el || el.disabled) return;
        el.focus();
        const min = order[t].endsWith('-h') ? 0 : 1;
        const caret = dir === 1 ? min : el.value.length;
        try { el.setSelectionRange(caret, caret); } catch { /* ignore */ }
    }
}
